import ResponsiveContainer from "../components/DensityResponsiveContainer";
import CsvExportButton from "../components/CsvExportButton";
import DashboardLoader, { DashboardContent } from "../components/DashboardLoader";
import { chartAxisTicks } from "../utils/chartAxisTicks";
import { layoutRect, layoutCoordinate, viewportHeight as layoutViewportHeight, viewportWidth as layoutViewportWidth } from "../utils/displayDensity";
/* eslint-disable */
import * as d3 from "d3";
import Papa from "papaparse";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { GalleryHorizontalEnd, Lock, RotateCcw, Grip } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import SteamVideoPreview from "../components/SteamVideoPreview";
import MindshareHeaderControls, { MarketMindIcon } from "../components/MindshareHeaderControls";
import useSeriesIntroAnimation from "../hooks/useSeriesIntroAnimation";
import SteamMarketMarks from "../components/SteamMarketMarks";
import { MGFilterControl, MGSectionHeader } from "../components/editorial/MGEditorial";
import { steamMarketDimensions } from "../services/steamMarketLayout";
import { requestSteamMarketLayout } from "../services/steamMarketLayoutRequest";
import { MARKET_PALETTE } from "../services/mindshareMarket";
import "../components/marketMind.css";
import "./steam.css";

const STEAM_VIEWS = [
  { mode: "cards", label: "Cards", Icon: GalleryHorizontalEnd },
  { mode: "market", label: "Market Mind", Icon: MarketMindIcon },
];
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,

  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DateSlider from "../components/DateSlider";
import "../components/gameArtCards.css";
import SteamChartLegend from "../components/SteamChartLegend";
import PriceSlider from "../components/PriceSlider";
import SavedViewsManager from "../components/SavedViewsManager";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import VirtualizedGameGrid from "../components/VirtualizedGameGrid";
import { useGlobalData } from "../contexts/DataContext";
import "./mindSHARE/styles/mindshare.css";


// ---- Steam price tiers (covers all games, no gaps) ----
const PRICE_TIERS = [
  { id: "free",    label: "Free",          min: 0.0,  max: 0.0 },
  { id: "under5",  label: "Under $5",      min: 0.01, max: 4.99 },
  { id: "5_9",     label: "$5 – $9.99",    min: 5.0,  max: 9.99 },
  { id: "10_14",   label: "$10 – $14.99",  min: 10.0, max: 14.99 },
  { id: "15_24",   label: "$15 – $24.99",  min: 15.0, max: 24.99 },
  { id: "25_39",   label: "$25 – $39.99",  min: 25.0, max: 39.99 },
  { id: "40_59",   label: "$40 – $59.99",  min: 40.0, max: 59.99 },
  { id: "60_plus", label: "$60+",          min: 60.0, max: 999.99 },
];

const PRICE_TIER_MAP = PRICE_TIERS.reduce((acc, t) => {
  acc[t.id] = t;
  return acc;
}, {});

const steamChartPillStyle = (active) => ({
  height: 32,
  padding: "0 12px",
  border: "none",
  borderRadius: 2,
  background: active ? "rgba(63,169,245,0.18)" : "#111",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 12,
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: active ? "inset 0 0 0 1px rgba(63,169,245,0.20)" : "none",
});

// Interpret Steam metadata full_price + f2p flag
function derivePrice(meta) {
  const raw = meta?.full_price;
  const f2pFlag = meta?.f2p;

  // f2p can be TRUE/FALSE, true/false, 1/0, etc.
  const isF2P =
    String(f2pFlag).toLowerCase() === "true" ||
    f2pFlag === 1 ||
    f2pFlag === "1";

  const n = Number(raw);

  if (isF2P) {
    // True free-to-play
    return { priceValue: 0, isF2P: true };
  }

  if (Number.isFinite(n) && n > 0) {
    // Normal paid game
    return { priceValue: n, isF2P: false };
  }

  // No usable price (0 with f2p = FALSE, or junk)
  return { priceValue: null, isF2P: false };
}

// --- Steam-only packed bubble skeleton (Market Mind) ---
const SteamBubbleSkeleton = ({ width = 800, height = 800, count = 220 }) => {
  // Make sure we get a square area so it looks like a disk
  const size = Math.max(200, Math.min(width, height));
  const SAFE_INSET = size * 0.06;
  const S = size - SAFE_INSET * 2;
  const offsetX = (width - S) / 2;
  const offsetY = (height - S) / 2;

  // Build nodes once for this width/height/count
  const nodes = useMemo(() => {
    // 1) Generate “weights” with a wide distribution so radii vary a lot
    const children = d3.range(count).map(() => {
      const r = Math.random();
      let exp;

      // a few giants in the middle
      if (r < 0.04) {
        exp = 8 + Math.random() * 2; // biggest
      }
      // some big mid-tier bubbles
      else if (r < 0.18) {
        exp = 5 + Math.random() * 2;
      }
      // chunky medium group
      else if (r < 0.45) {
        exp = 3 + Math.random() * 2;
      }
      // lots of small ones
      else {
        exp = 0 + Math.random() * 2;
      }

      return { value: Math.pow(1.5, exp) };
    });

    const root = d3.hierarchy({ children }).sum((d) => d.value);
    d3.pack().size([S, S]).padding(2.5)(root);

    // 2) Turn leaves into renderable nodes with per-circle animation
    return root.leaves().map((leaf, i) => {
      // tiny random radius jitter so the disk edge isn’t perfectly smooth
      const jitter = 0.92 + Math.random() * 0.22;
      const cx = leaf.x + offsetX;
      const cy = leaf.y + offsetY;

      // random pulse timing per bubble
      const delay = Math.random() * 1.8;               // 0–1.8s
      const duration = 1.2 + Math.random() * 0.9;      // 1.2–2.1s
      const opacity = 0.30 + Math.random() * 0.28;     // 0.30–0.58

      return {
        id: i,
        cx,
        cy,
        r: leaf.r * jitter,
        opacity,
        animation: `ms-skeleton-pulse ${duration.toFixed(
          2
        )}s ease-in-out ${delay.toFixed(2)}s infinite`,
      };
    });
  }, [S, offsetX, offsetY, count]);

  return (
    <svg
      className="ms-bubble-skeleton"
      width={width}
      height={height}
      aria-label="Loading"
    >
      {nodes.map((n) => (
        <circle
          key={n.id}
          cx={n.cx}
          cy={n.cy}
          r={n.r}
          fill="#1d2730"
          opacity={n.opacity}
          style={{
            // per-bubble pulse: same keyframes, different timing
            animation: n.animation,
            transformOrigin: `${n.cx}px ${n.cy}px`,
          }}
        />
      ))}
    </svg>
  );
};


/* ============================
   .csv.gz helpers for Followers
   ============================ */
const rIC =
  typeof window !== "undefined" && "requestIdleCallback" in window
    ? window.requestIdleCallback
    : (cb) => setTimeout(() => cb({ timeRemaining: () => 17 }), 0);

const LATEST_FOLLOWERS_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/latest_followers.csv.gz";
const FOLLOWERS_GROWTH_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/followers_growth.csv.gz";
const LATEST_CCU_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/latest_ccu.csv.gz";

/* -------- CCU history backend (same as GameDetail) -------- */
import { loadSteamHistory } from "../services/steamChartHistory";

// North America prime time:
// 11:00 PM UTC  →  3:00 PM PT
// 05:00 AM UTC* →  9:00 PM PT (*next UTC day, same local day)
// i.e. 23:00 → 05:00 UTC, wraps midnight
const NA_UTC_START_HOUR = 24;
const NA_UTC_END_HOUR   = 6;   // wrap to next day
const NA_BAND_FILL      = "rgba(63, 169, 245, 0.16)"; // blue

// APAC prime time:
// 10:00 AM UTC →  2:00 AM PT
// 04:00 PM UTC →  8:00 AM PT
// i.e. 10:00 → 16:00 UTC
const ASIA_UTC_START_HOUR = 10;
const ASIA_UTC_END_HOUR   = 16;
const ASIA_BAND_FILL      = "rgba(255, 235, 59, 0.1)"; // yellow

// Europe prime time:
// 5:00 PM UTC →  9:00 AM PT
// 11:00 PM UTC →  3:00 PM PT
// i.e. 11:00 → 17:00 UTC
const EU_UTC_START_HOUR = 17;
const EU_UTC_END_HOUR   = 23;
const EU_BAND_FILL      = "rgba(76, 175, 80, 0.14)";   // (currently green)

const fmtDate = (dt) =>
  new Date(dt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
const fmtDateTimeUTC = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);

  // Example: 12/13 02:01AM (UTC)
  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
};

const fmtDateShortUTC = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);

  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    timeZone: "UTC",
  });
};

// ✅ Parse MySQL DATETIME (no tz) as UTC so daily bucketing is stable
function parseMysqlDatetimeAsUTC(dt) {
  if (!dt) return null;
  let raw = String(dt).trim();

  // If it already has timezone info, respect it
  const hasTZ = /[zZ]$|[+\-]\d\d:?\d\d$/.test(raw);

  // "YYYY-MM-DD HH:MM:SS" -> "YYYY-MM-DDTHH:MM:SS"
  if (!raw.includes("T")) raw = raw.replace(" ", "T");

  const d = hasTZ ? new Date(raw) : new Date(raw + "Z"); // force UTC
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ===== live + daily CCU helpers (copied from GameDetail) ===== */
const dayKeyUTC = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear(),
    m = d.getUTCMonth(),
    dd = d.getUTCDate();
  return new Date(Date.UTC(y, m, dd)).toISOString().slice(0, 10);
};
const startOfDayUTCISO = (dayStr) => `${dayStr}T00:00:00.000Z`;

function normalizeArchive(rows) {
  return (rows || [])
    .map((r) => ({
      dt: startOfDayUTCISO(dayKeyUTC(r.dt)),
      ccu: Number(r.ccu) || 0,
    }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}
function dailyFromLiveSnapshots(rows) {
  const byDay = new Map();
  for (const r of rows || []) {
    const k = dayKeyUTC(r.dt);
    const v = Number(r.ccu);
    if (!k || !Number.isFinite(v) || v < 0) continue;
    const prev = byDay.get(k);
    if (prev === undefined || v > prev) byDay.set(k, v);
  }
  return Array.from(byDay.entries())
    .map(([day, max]) => ({ dt: startOfDayUTCISO(day), ccu: max }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}

function mergeDailyWithLiveDaily(dailySeries, liveRows) {
  const base = Array.isArray(dailySeries) ? [...dailySeries] : [];
  const liveDaily = dailyFromLiveSnapshots(liveRows);

  const byDt = new Map(base.map((p) => [p.dt, { ...p }]));
  for (const p of liveDaily) {
    byDt.set(p.dt, { ...p }); // overwrite/add (includes partial UTC day)
  }

  return Array.from(byDt.values()).sort(
    (a, b) => new Date(a.dt) - new Date(b.dt)
  );
}

function forwardFill(series) {
  let last = null;
  for (let i = 0; i < series.length; i++) {
    const v = Number(series[i].ccu);
    if (Number.isFinite(v) && v > 0) {
      last = v;
    } else if (last != null) {
      series[i].ccu = last;
    }
  }
  return series;
}

function normalizeLive(rows) {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const floor = now - sevenDaysMs;

  const out = [];
  for (const r of rows || []) {
    // Always interpret dt as UTC if it has no explicit timezone
let raw = String(r.dt).trim();

// If it already has a timezone (Z or +hh:mm / -hh:mm), just use it
const hasTZ = /[zZ]$|[+\-]\d\d:?\d\d$/.test(raw);

// Normalize "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM:SS"
if (!raw.includes("T")) {
  raw = raw.replace(" ", "T");
}

let d;
if (hasTZ) {
  d = new Date(raw);
} else {
  // Treat as UTC by appending Z
  d = new Date(raw + "Z");
}

const t = d.getTime();
if (!Number.isFinite(t) || t < floor) continue;


    let v = Number(r.ccu);
    if (!(Number.isFinite(v) && v > 0)) v = NaN;

    out.push({ dt: d.toISOString(), ccu: v });
  }
  out.sort((a, b) => new Date(a.dt) - new Date(b.dt));
  return forwardFill(out);
}

async function loadLiveSeries(appId, hours = 24 * 7) {
  const live = await loadSteamHistory(`/live_ccu/${appId}?hours=${hours}`);
  if (Array.isArray(live) && live.length) return normalizeLive(live);
  return [];
}

async function loadDailySeries(appId) {
  const [daily, liveNorm] = await Promise.all([
    loadSteamHistory(`/ccu_daily/${appId}`),
    loadLiveSeries(appId),
  ]);
  let base = Array.isArray(daily) && daily.length ? normalizeArchive(daily) : null;

  if (!base) {
    const arch = await loadSteamHistory(`/ccu_history/${appId}`);
    if (Array.isArray(arch) && arch.length) base = normalizeArchive(arch);
  }

  if (!base) {
    // If we have no archive at all, at least return live-derived daily
    return dailyFromLiveSnapshots(liveNorm);
  }

  // ✅ Merge: ensures today’s UTC day exists in daily series
  return mergeDailyWithLiveDaily(base, liveNorm);
}

/* ===== Followers RAW series (all points, sorted by timestamp, UTC) ===== */
async function loadFollowersSeries(appId, days = 365) {
  const rows = await loadSteamHistory(`/followers_history/${appId}?days=${days}`);
  if (!Array.isArray(rows) || !rows.length) return [];

  const out = [];

  for (const r of rows) {
    if (!r?.dt) continue;

    const d = parseMysqlDatetimeAsUTC(r.dt);
    if (!d) continue;

    const val = Number(r.followers ?? r.Followers);
    if (!Number.isFinite(val)) continue;

    // keep raw timestamp (UTC ISO)
    out.push({
      dt: d.toISOString(),
      ccu: val, // chart uses ccu key, but it's followers
    });
  }

  // sort by timestamp ascending
  out.sort((a, b) => new Date(a.dt) - new Date(b.dt));
  return out;
}

/* Live/Daily rows → chart points (same as GameDetail) */
function toChartRows(game, mode, viewByDaysSinceLaunch) {
  if (!game?.metadata) return [];
  if (mode === "live") {
    if (!Array.isArray(game.live) || game.live.length === 0) return [];
    return game.live.map((r) => ({ x: r.dt, [game.metadata.app_id]: r.ccu }));
  }
  if (!Array.isArray(game.daily) || game.daily.length === 0) return [];
  const release = new Date(game.metadata.release_date);
  const relValid = !Number.isNaN(release.getTime());
  return game.daily.map((r) => {
    const d = new Date(r.dt);
    const key =
      viewByDaysSinceLaunch && relValid
        ? Math.max(0, Math.floor((d - release) / (1000 * 60 * 60 * 24)))
        : r.dt; // ✅ keep UTC ISO, e.g. "2025-12-13T00:00:00.000Z"
    return { x: key, [game.metadata.app_id]: r.ccu };
  });
}

// Decide average daily playtime per user (hours) based on
// 1) a global baseline and
// 2) CCU shape (spikiness)
// 3) Days since release
// Decide average daily playtime per user (hours) based on
// 1) a global baseline
// 2) CCU shape (spikiness)
// 3) Days since release
// 4) Uncertainty band width (spread) that varies by game
function getDynamicSessionHoursForGame(game) {
  // --- 0) Baseline for Steam (same as before) ---
  let low = 3.0;   // conservative
  let high = 3.5;  // aggressive

  const live = Array.isArray(game?.live) ? game.live : [];

  // We'll reuse these for uncertainty later
  let ratio = null;      // max / mean spikiness
  let daysSince = null;  // days since release

  // --- 1) Shape / spikiness from last 24h of live CCU ---
  if (live.length >= 8) {
    const ccus = live
      .map((r) => Number(r.ccu) || 0)
      .filter((v) => v > 0);

    if (ccus.length >= 8) {
      const sum = ccus.reduce((a, b) => a + b, 0);
      const mean = sum / ccus.length;
      const max = Math.max(...ccus);

      if (mean > 0 && max > 0) {
        ratio = max / mean; // 1.0 = flat, >1 = peaky

        // Map ratio into a factor for the *center*:
        // - very flat (ratio <= 1.3)  → +10% hours
        // - very spiky (ratio >= 1.9) → -20% hours
        // - linear in between
        let shapeFactor;
        if (ratio <= 1.3) {
          shapeFactor = 1.10;
        } else if (ratio >= 1.9) {
          shapeFactor = 0.80;
        } else {
          // interpolate between 1.10 and 0.80 as ratio goes 1.3 → 1.9
          const t = (ratio - 1.3) / (1.9 - 1.3);
          shapeFactor = 1.10 + t * (0.80 - 1.10);
        }

        low *= shapeFactor;
        high *= shapeFactor;
      }
    }
  }

  // --- 2) Days since release (launch vs long tail) ---
  const rdStr = game?.metadata?.release_date;
  if (rdStr) {
    const rd = new Date(rdStr);
    const now = new Date();
    if (!Number.isNaN(rd.getTime())) {
      daysSince = (now - rd) / (1000 * 60 * 60 * 24);

      // First 30 days: people binge more → longer sessions
      if (daysSince >= 0 && daysSince <= 30) {
        low *= 1.10;   // +10% hours
        high *= 1.10;
      }

      // Very long tail (1+ years): more drop-in behavior → slightly shorter
      if (daysSince > 365) {
        low *= 0.90;   // -10% hours
        high *= 0.90;
      }
    }
  }

  // --- 3) Convert to center + spread, then vary spread by uncertainty ---
  let center = (low + high) / 2;
  let spread = (high - low) / 2; // base half-width (~0.25 scaled by factors)

  // Bounds for how wide the band can get
  const MIN_SPREAD = 0.20;
  const MAX_SPREAD = 0.80;

  // 3a) Spikiness: peakier games → wider uncertainty band
  if (ratio != null) {
    // Flat (≤1.3) → narrow band; spiky (≥1.9) → wide band
    if (ratio <= 1.3) {
      spread *= 0.8; // tighten by 20%
    } else if (ratio >= 1.9) {
      spread *= 1.8; // almost 2× wider
    } else {
      const t = (ratio - 1.3) / (1.9 - 1.3);
      const factor = 0.8 + t * (1.8 - 0.8); // 0.8 → 1.8
      spread *= factor;
    }
  }

  // 3b) Recency: launch window → more uncertainty; long tail → less
  if (daysSince != null) {
    if (daysSince >= 0 && daysSince <= 30) {
      // first month: widen band a bit more
      spread *= 1.25;
    } else if (daysSince > 365) {
      // long tail: slightly narrower
      spread *= 0.9;
    }
  }

  // Clamp spread to sane bounds, then recompute low/high around center
  spread = Math.max(MIN_SPREAD, Math.min(spread, MAX_SPREAD));

  low = center - spread;
  high = center + spread;

  // --- 4) Final safety clamps on absolute hours ---
  low = Math.max(1.5, Math.min(low, 6.0));
  high = Math.max(low, Math.min(high, 7.0));

  return { lowHours: low, highHours: high };
}

// Estimate Daily Users from live CCU snapshots over the last 24h
function estimateDailyUsersFromLive(
  liveRows,
  hoursLow = 2.25,  // lower bound of average hours per user
  hoursHigh = 2.5   // upper bound of average hours per user
) {
  if (!Array.isArray(liveRows) || liveRows.length < 2) return null;

  const MS_PER_HOUR = 60 * 60 * 1000;
  const WINDOW_HOURS = 24;
  const WINDOW_MS = WINDOW_HOURS * MS_PER_HOUR;

  // Ensure we’re sorted by time
  const sorted = [...liveRows]
    .map((r) => {
      const t = new Date(r.dt).getTime();
      return {
        t,
        ccu: Number(r.ccu) || 0,
      };
    })
    .filter((r) => Number.isFinite(r.t))
    .sort((a, b) => a.t - b.t);

  if (sorted.length < 2) return null;

  const maxTs = sorted[sorted.length - 1].t;
  const windowStart = maxTs - WINDOW_MS;

  let totalPlayerHours = 0;

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (!Number.isFinite(prev.t) || !Number.isFinite(curr.t)) continue;
    if (curr.t <= prev.t) continue;

    // overlap of this segment with the [windowStart, maxTs] window
    const segStart = Math.max(prev.t, windowStart);
    const segEnd   = Math.min(curr.t, maxTs);
    const segMs    = segEnd - segStart;
    if (segMs <= 0) continue;

    const avgCCU   = (prev.ccu + curr.ccu) / 2;
    const segHours = segMs / MS_PER_HOUR;
    totalPlayerHours += avgCCU * segHours;
  }

  if (totalPlayerHours <= 0) return null;

  // --- Base (unbiased) estimates from your dynamic hours ---
  const baseLowUsers  = totalPlayerHours / hoursHigh; // fewer users (more hours per user)
  const baseHighUsers = totalPlayerHours / hoursLow;  // more users (fewer hours per user)

  if (!Number.isFinite(baseLowUsers) || !Number.isFinite(baseHighUsers)) {
    return null;
  }

  // If somehow inverted, just collapse to one point
  if (baseHighUsers <= 0 || baseLowUsers <= 0) return null;

  const center = (baseLowUsers + baseHighUsers) / 2;
  const width  = baseHighUsers - baseLowUsers;

  // --- 1) Tighter band ---
  // e.g. 0.6 → 60% of original width (you can tweak 0.6 → 0.5 if you want even tighter)
  const SHRINK = 0.6;

  let half = (width * SHRINK) / 2;
  if (!Number.isFinite(half) || half < 0) half = 0;

  let low  = center - half;
  let high = center + half;

  // --- 2) Bias both ends downward so we under-estimate a bit ---
  // e.g. 0.9 → shift ~10% lower
  const BIAS = 0.9;
  low  *= BIAS;
  high *= BIAS;

  low  = Math.round(low);
  high = Math.round(high);

  if (!Number.isFinite(low) || !Number.isFinite(high) || low <= 0 || high <= 0) {
    return null;
  }

  // Make sure ordering is correct
  const finalLow  = Math.min(low, high);
  const finalHigh = Math.max(low, high);

  return {
    low: finalLow,
    high: finalHigh,
  };
}


// Nicely round the Y-axis max
function roundYAxisMax(rawMax) {
  const max = Number(rawMax);
  if (!Number.isFinite(max)) return rawMax;

  // Keep your current 10% headroom
  const padded = max * 1.1;

  // For small games, keep it precise-ish
  if (padded <= 100) {
    return Math.ceil(padded); // 67.042 -> 68, etc.
  }

  // For anything bigger, round UP to the next hundred
  // e.g., 1,694,621 * 1.1 = 1,864,083.1 -> 1,864,100
  return Math.ceil(padded / 100) * 100;
}

/** Fetch a .csv.gz and return CSV text (native DecompressionStream if available). */
async function fetchGzipCsvText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);
  if ("DecompressionStream" in window && res.body) {
    try {
      const ds = new DecompressionStream("gzip");
      const stream = res.body.pipeThrough(ds);
      const reader = stream.getReader();
      const chunks = [];
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      const blob = new Blob(chunks, { type: "text/plain;charset=utf-8" });
      return await blob.text();
    } catch (e) {
      try {
        return await res.text();
      } catch {
        throw e;
      }
    }
  }
  return await res.text();
}

/** Parse CSV text to array of objects (Papa header mode). */
function parseCsvText(csvText) {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      worker: true, 
      complete: ({ data }) => resolve(data),
      error: (err) => reject(err),
    });
  });
}

/** fetch + gunzip (if needed) + parse */
async function loadCsvGz(url) {
  const text = await fetchGzipCsvText(url);
  return await parseCsvText(text);
}

/* ========= NEW date-only helpers (local time) ========= */
function toLocalISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function safeDate(dLike) {
  if (!dLike) return null;
  const d = new Date(dLike);
  return Number.isNaN(d.getTime()) ? null : d;
}
// "Contains" search, but only starting at a word boundary — plain .includes()
// lets "resident" match inside "president", "evil" match inside "medieval", etc.
function nameMatchesSearch(name, term) {
  const q = (term || "").trim().toLowerCase();
  if (!q) return true;
  const n = (name || "").toLowerCase();
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z0-9])${escaped}`).test(n);
}
function withinLastNDays(dateLike, n = 7) {
  const d = safeDate(dateLike);
  if (!d) return false;
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - n);
  const dISO = toLocalISODate(d);
  const startISO = toLocalISODate(start);
  const endISO = toLocalISODate(today);
  return dISO >= startISO && dISO <= endISO;
}
function bucketFirstLastPerUtcDay(rawRows) {
  // rawRows is already sorted ascending by dt in loadFollowersSeries()
  const byDay = new Map(); // day -> { firstX, firstV, lastX, lastV }

  for (const r of rawRows || []) {
    const day = dayKeyUTC(r.dt);
    const v = Number(r.ccu);
    if (!day || !Number.isFinite(v)) continue;

    const prev = byDay.get(day);
    if (!prev) {
      byDay.set(day, { firstX: r.dt, firstV: v, lastX: r.dt, lastV: v });
    } else {
      // overwrite as we move forward -> last-of-day
      prev.lastX = r.dt;
      prev.lastV = v;
    }
  }

  // sorted by day
  const days = Array.from(byDay.keys()).sort();
  return { byDay, days };
}

/* ================================================ */

function releasedWithin7Days(releaseDate) {
  // NEW: delegate to local date-only window
  return withinLastNDays(releaseDate, 7);
}

/** =========================================================
 *  Effervescent (Login-style) bubbles for Steam games (CCU)
 *  ========================================================= */
function EffervescentBubbles({ games, onHoverChange }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const bubblesRef = useRef([]);
  const pausedRef = useRef(false);

  // Round-robin source
  const sorted = useMemo(
    () => [...games].sort((a, b) => (b.ccu || 0) - (a.ccu || 0)),
    [games]
  );
  const idxRef = useRef(0);
  const nextGame = () => {
    if (!sorted.length) return null;
    const g = sorted[idxRef.current];
    idxRef.current = (idxRef.current + 1) % sorted.length;
    return g;
  };

  // Spawn accumulator
  const spawnRateRef = useRef(48);
  const spawnAccRef = useRef(0);
  const warmUntilRef = useRef(0);

  // Helpers
  const lerp = (a, b, t) => a + (b - a) * t;
  const hexToRgb = (h) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
    return m
      ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
      : { r: 255, g: 255, b: 255 };
  };
  const piece = (colors, t) => {
    const n = colors.length - 1;
    const x = Math.max(0, Math.min(1, t)) * n;
    const i = Math.floor(x);
    const f = x - i;
    const c1 = hexToRgb(colors[i]);
    const c2 = hexToRgb(colors[Math.min(n, i + 1)]);
    const r = Math.round(lerp(c1.r, c2.r, f));
    const g = Math.round(lerp(c1.g, c2.g, f));
    const b = Math.round(lerp(c1.b, c2.b, f));
    return `rgb(${r},${g},${b})`;
  };
  const mixWithWhite = (rgbStr, amt = 0.25) => {
    const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgbStr);
    if (!m) return rgbStr;
    const r = Math.round(lerp(parseInt(m[1]), 255, amt));
    const g = Math.round(lerp(parseInt(m[2]), 255, amt));
    const b = Math.round(lerp(parseInt(m[3]), 255, amt));
    return `rgb(${r},${g},${b})`;
  };
  const toRgba = (rgbStr, a) => {
    const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgbStr);
    if (!m) return `rgba(255,255,255,${a})`;
    return `rgba(${m[1]},${m[2]},${m[3]},${a})`;
  };

  const ccMax = Math.max(1000, ...games.map((g) => g.ccu || 0));
  const colorForCCU = (ccu) => {
    const v = Math.max(0, ccu || 0);
    if (v < 100) return piece(["#ff3b2f", "#ff6a4f"], v / 100);
    if (v < 1000) return piece(["#fe6d4e", "#ffc28b"], (v - 100) / 900);
    const t = Math.log10(v) - Math.log10(1000);
    const d = Math.max(1e-6, Math.log10(ccMax) - Math.log10(1000));
    return piece(
      ["#819898", "#4c7d8e", "#174d7c", "#2794c3"],
      Math.min(1, t / d)
    );
  };

  const getRadius = (ccu, w) => {
    const maxR = Math.max(90, Math.min(170, (w || layoutViewportWidth()) * 0.07));
    const minR = Math.max(11, Math.min(24, (w || layoutViewportWidth()) * 0.012));
    const t = Math.sqrt((ccu || 0) / ccMax);
    return lerp(minR, maxR, t);
  };

  const hitTest = (x, y) => {
    const arr = bubblesRef.current;
    for (let i = arr.length - 1; i >= 0; i--) {
      const b = arr[i];
      const dx = x - b.x;
      const dy = y - b.y;
      if (dx * dx + dy * dy <= b.r * b.r) return b;
    }
    return null;
  };

  // Hover control
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMove = (e) => {
      const rect = layoutRect(canvas);
      const x = layoutCoordinate(e.clientX) - rect.left;
      const y = layoutCoordinate(e.clientY) - rect.top;
      const hit = hitTest(x, y);
      pausedRef.current = !!hit;
      onHoverChange?.(
        hit ? { x: hit.x, y: hit.y, r: hit.r, data: hit.data } : null
      );
    };
    const handleLeave = () => {
      pausedRef.current = false;
      onHoverChange?.(null);
    };

    canvas.addEventListener("mousemove", handleMove);
    canvas.addEventListener("mouseleave", handleLeave);
    return () => {
      canvas.removeEventListener("mousemove", handleMove);
      canvas.removeEventListener("mouseleave", handleLeave);
    };
  }, [onHoverChange]);

  // Draw loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true, desynchronized: true });

    const fit = () => {
      const parent = canvas.parentElement;
      const w = parent?.clientWidth || layoutViewportWidth();
      const h = parent?.clientHeight || layoutViewportHeight();
      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(canvas.parentElement || document.body);

    const rand = (a, b) => a + Math.random() * (b - a);
    const maxBubbles = 240;

    const spawnOne = (opts = {}) => {
      if (bubblesRef.current.length >= maxBubbles) return;
      const g = nextGame();
      if (!g) return;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const r = getRadius(g.ccu, w);
      const seeded = !!opts.seeded;

      const x = rand(r, Math.max(r, w - r));
      let y;
      if (seeded) {
        const bandTop = h * 0.85;
        const bandBottom = Math.min(h - r * 0.4, h * 0.96);
        y = rand(bandTop, bandBottom);
      } else {
        y = h + r + rand(0, h * 0.18);
      }

      const base = ((cc) => {
        const refR = getRadius(cc, w);
        return lerp(30, 88, r / Math.max(1, refR));
      })(Math.max(...games.map((g) => g.ccu || 0), 1));

      const speed = base * lerp(0.96, 1.08, Math.random());
      const vx = rand(-10, 10);
      const stopY = rand(h * 0.06, h * 0.12);

      bubblesRef.current.push({
        x,
        y,
        r,
        vx,
        vy: -speed,
        life: seeded ? rand(0.12, 0.6) : 0,
        lifeMax: 60,
        color: colorForCCU(g.ccu),
        name: g.name || "",
        data: g,
        wobblePhase: rand(0, Math.PI * 2),
        wobbleAmp: rand(0.25, 1.0),
        stopY,
      });
    };

    const seedInitial = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const area = w * h || 1;
      const target = Math.min(
        maxBubbles * 0.4,
        Math.max(20, Math.floor(area / 42000))
      );
      for (let i = 0; i < target; i++) spawnOne({ seeded: true });
    };

    warmUntilRef.current = performance.now() + 1200;
    seedInitial();

    let last = performance.now();
    const tick = () => {
      const t = performance.now();
      const dt = Math.min(0.05, (t - last) / 1000);
      last = t;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      ctx.clearRect(0, 0, w, h);

      if (!pausedRef.current) {
        const warmBoost = t < warmUntilRef.current ? 2.0 : 1;
        spawnAccRef.current += dt * spawnRateRef.current * warmBoost;
        while (spawnAccRef.current >= 1) {
          spawnOne();
          spawnAccRef.current -= 1;
        }
      }

      const arr = bubblesRef.current;
      arr.sort((a, b) => a.r - b.r);

      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.imageSmoothingEnabled = true;

      for (let i = arr.length - 1; i >= 0; i--) {
        const b = arr[i];

        if (!pausedRef.current) {
          b.life += dt;
          const wob = Math.sin(b.wobblePhase + b.life * 1.6) * b.wobbleAmp;
          b.x += b.vx * dt + wob;
          b.y += b.vy * dt;

          if (b.x < b.r) {
            b.x = b.r;
            b.vx = Math.abs(b.vx) * 0.9;
          }
          if (b.x > w - b.r) {
            b.x = w - b.r;
            b.vx = -Math.abs(b.vx) * 0.9;
          }
        }

        const atTopBand = b.y - b.r <= b.stopY;
        const aIn = Math.min(1, b.life / 0.5);
        const aOutByLife = Math.min(1, (b.lifeMax - b.life) / 2.8);
        const aOutByTop = atTopBand
          ? Math.max(0, (b.y - b.stopY) / (h * 0.06))
          : 1;
        const alpha =
          Math.max(
            0,
            Math.min(1, Math.min(aIn, Math.max(aOutByLife, aOutByTop)))
          ) * 0.9;

        if (b.y + b.r < -20 || (atTopBand && alpha < 0.06)) {
          arr.splice(i, 1);
          continue;
        }

        const r = b.r;

        const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, r);
        grad.addColorStop(0.0, toRgba(b.color, 0.12));
        grad.addColorStop(0.82, toRgba(b.color, 0.4));
        grad.addColorStop(0.96, toRgba(mixWithWhite(b.color, 0.15), 0.85));
        grad.addColorStop(1.0, "rgba(0,0,0,0)");
        ctx.globalAlpha = alpha;
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = alpha * 0.65;
        ctx.lineWidth = Math.max(1, r * 0.035);
        ctx.strokeStyle = toRgba(mixWithWhite(b.color, 0.2), 0.6);
        ctx.beginPath();
        ctx.arc(b.x, b.y, r * 0.988, 0, Math.PI * 2);
        ctx.stroke();

        let label = b.name || "";
        const maxLen = r > 64 ? 28 : r > 44 ? 22 : 12;
        if (label.length > maxLen) label = label.slice(0, maxLen) + "…";

        const fontSize = Math.max(6, Math.min(12, r * 0.34));

        ctx.save();
        ctx.globalAlpha = 1;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineJoin = "round";
        ctx.miterLimit = 2;
        ctx.font = `500 ${fontSize}px "Segoe UI", system-ui, -apple-system, sans-serif`;
        ctx.strokeStyle = "rgba(0,0,0,0.50)";
        ctx.lineWidth = Math.max(0.75, fontSize * 0.09);
        ctx.strokeText(label, b.x, b.y);
        ctx.fillStyle = "#fff";
        ctx.fillText(label, b.x, b.y);
        ctx.restore();
      }

      ctx.restore();
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      bubblesRef.current = [];
      onHoverChange?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "auto",
          background:
            "radial-gradient(1200px 600px at 50% -10%, rgba(40,144,195,0.10), transparent 55%)",
        }}
        aria-hidden
      />
    </div>
  );
}

/** Utility: place a 320px popover near (x,y) within a container rect. */
function computePopoverPos({
  x,
  y,
  r,
  containerWidth,
  containerHeight,
  preferBelow,
}) {
  const CARD_W = 320;
  const CARD_H = 360; // approximate card height (image + text)
  const GAP = 16;

  // --- Horizontal placement (unchanged) ---
  let preferRight = x < containerWidth * 0.5;
  let left = preferRight ? x + r + GAP : x - r - CARD_W - GAP;

  // If initial side would push us off-screen, flip
  if (left < 8) {
    preferRight = true;
    left = x + r + GAP;
  } else if (left + CARD_W > containerWidth - 8) {
    preferRight = false;
    left = x - r - CARD_W - GAP;
  }

  // Final clamp within viewport
  left = Math.max(8, Math.min(left, containerWidth - CARD_W - 8));

  // --- Vertical placement (tiered by height) ---
  // Top third  -> card mostly below bubble
  // Middle     -> card centered on bubble
  // Bottom third -> card mostly above bubble
  const minTop = 8;
  const maxTop = containerHeight - CARD_H - 8;
  const yFrac = containerHeight > 0 ? y / containerHeight : 0.5;

  let rawTop;
  if (yFrac < 0.33) {
    // upper third: 3/4 of card below center
    rawTop = y - CARD_H * 0.25;
  } else if (yFrac < 0.66) {
    // middle third: center card on bubble
    rawTop = y - CARD_H * 0.5;
  } else {
    // bottom third: 3/4 of card above center (keeps it from sitting on bottom edge)
    rawTop = y - CARD_H * 0.9;
  }

  const top = Math.max(minTop, Math.min(rawTop, maxTop));

  return { left, top };
}



/* ===== GoldGlow ring (outer) ===== */
function GoldGlow({ active, subtle = false }) {
  if (!active) return null;

  // Strong vs subtle glow settings
  const borderColor = subtle
    ? "rgba(255,215,0,0.7)"
    : "rgba(255,215,0,0.95)";

  const boxShadow = subtle
    ? "0 0 0 1px rgba(255,215,0,0.7), 0 0 16px 3px rgba(255,200,0,0.30)"
    : "0 0 0 1px rgba(255,215,0,0.9), 0 0 28px 6px rgba(255,200,0,0.45)";

  const background = subtle
    ? "radial-gradient(120% 120% at 50% 50%, rgba(255,215,0,0.08), rgba(255,215,0,0) 70%)"
    : "radial-gradient(130% 130% at 50% 50%, rgba(255,215,0,0.14), rgba(255,215,0,0) 70%)";

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "2px",
        pointerEvents: "none",
        zIndex: 5,
        border: `2px solid ${borderColor}`,
        boxShadow,
        background,
        mixBlendMode: "screen",
      }}
    />
  );
}



function PlayersHeader({
  steamTotalOnline,
  totalCcu,
  playersShareOfTotal,
  filteredPlayers,
  statNumberStyle,
  showOnline,
  onRefresh,
  refreshReady,
  onOpenFilters,
  chartMode = false,   // 👈 NEW
  loading = false,
  loadingPercent = 0,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
      }}
    >
      {/* LEFT: stats block(s) */}
      {/* In chart mode: only Playing Now + % of Players */}
      {chartMode ? (
        <>
          <div className="steam-stat-chip">
            <span style={statNumberStyle}>{totalCcu.toLocaleString()}</span>
            <span className="steam-stat-label">Playing Now</span>
          </div>

          <div className="steam-stat-chip">
            <span style={statNumberStyle}>{playersShareOfTotal}%</span>
            <span className="steam-stat-label">of Players</span>
          </div>

        </>
      ) : (
        <>
          {showOnline && (
            <div className="steam-stat-chip">
              <span style={statNumberStyle}>
                {steamTotalOnline.toLocaleString()}
              </span>
              <span className="steam-stat-label">Online</span>
            </div>
          )}

          <div className="steam-stat-chip">
            <span style={statNumberStyle}>{totalCcu.toLocaleString()}</span>
            <span className="steam-stat-label">Playing Now</span>
          </div>

          <div className="steam-stat-chip">
            <span style={statNumberStyle}>{playersShareOfTotal}%</span>
            <span className="steam-stat-label">of Players</span>
          </div>
        </>
      )}

      {loading && (
        <div className="steam-loading-stat" role="status" aria-live="polite">
          <span className="steam-loading-stat-label">Loading data</span>
          <span className="steam-loading-stat-value">{Math.round(loadingPercent)}%</span>
          <span className="steam-loading-stat-track"><span style={{ width: `${Math.max(3, Math.min(99, loadingPercent))}%` }} /></span>
        </div>
      )}

      {/* RIGHT: Active Games (only when NOT chart mode) + refresh button (always) */}
      <div
        style={{
          fontSize: "1rem",
          color: "#aaa",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {!chartMode && (
          <div className="steam-stat-chip">
            <span style={statNumberStyle}>
              {filteredPlayers.length.toLocaleString()}
            </span>
            <span className="steam-stat-label">Active Games</span>
          </div>
        )}

        <button
          type="button"
          onClick={onRefresh}
          title="Refresh Steam data"
          style={{
            width: 28,
            height: 28,
            borderRadius: "999px",
            border: "1px solid #555",
            background: "#222",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: refreshReady ? "pointer" : "default",
            padding: 0,
            lineHeight: 1,
            opacity: refreshReady ? 1 : 0,
            pointerEvents: refreshReady ? "auto" : "none",
            transition: "opacity 300ms ease",
          }}
        >
          ↻
        </button>

        <button
          type="button"
          onClick={onOpenFilters}
          title="Open filter on hover"
          aria-label="Open filter on hover"
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            border: "1px solid #555",
            background: "#111",
            color: "#dfe7ef",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.02)",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function FollowersHeader({
  filteredFollowers,
  followersTotal,
  gamesFilter,
  setGamesFilter,
  followersSort,
  setFollowersSort,
  statNumberStyle,
  onOpenFilters,
  chartMode = false, 
}) {
  const pillBase = {
    border: "none",
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
    transition: "background 0.15s ease",
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        width: "100%",
        flexWrap: "wrap",
      }}
    >
      {/* Left: stats */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 18,
          flexWrap: "wrap",
        }}
      >
        {chartMode ? (
          // Chart mode: just Total Follows, based on chart selection
          <div style={{ fontSize: "1rem", color: "#aaa" }}>
            <span style={statNumberStyle}>
              {followersTotal.toLocaleString()}
            </span>
            <span style={{ marginLeft: 6 }}>Total Follows</span>
          </div>
        ) : (
          // Normal mode: Games + Total Follows
          <>
            <div style={{ fontSize: "1rem", color: "#aaa" }}>
              <span style={statNumberStyle}>
                {filteredFollowers.length.toLocaleString()}
              </span>
              <span style={{ marginLeft: 6 }}>
                {gamesFilter === "unreleased" ? "Upcoming Games" : "Games"}
              </span>
            </div>
            <div style={{ fontSize: "1rem", color: "#aaa" }}>
              <span style={statNumberStyle}>
                {followersTotal.toLocaleString()}
              </span>
              <span style={{ marginLeft: 6 }}>Total Follows</span>
            </div>
          </>
        )}
      </div>

      {/* Right: filter + sort pills */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {/* All | Unreleased */}
        <div
          role="tablist"
          aria-label="Games filter"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            background: "transparent",
            border: "1px solid #333",
            borderRadius: 999,
            padding: 4,
            minWidth: 260,
          }}
        >
          <button
            role="tab"
            aria-selected={gamesFilter === "all"}
            onClick={() => setGamesFilter("all")}
            style={{
              ...pillBase,
              background: gamesFilter === "all" ? "#3fa9f5" : "transparent",
              color: gamesFilter === "all" ? "#000" : "#ddd",
            }}
          >
            All
          </button>
          <button
            role="tab"
            aria-selected={gamesFilter === "unreleased"}
            onClick={() => setGamesFilter("unreleased")}
            style={{
              ...pillBase,
              background: gamesFilter === "unreleased" ? "#3fa9f5" : "transparent",
              color: gamesFilter === "unreleased" ? "#000" : "#ddd",
            }}
          >
            Unreleased
          </button>
        </div>

        {/* Divider */}
        <span aria-hidden="true" style={{ color: "#444", fontWeight: 700 }}>|</span>

        {/* Sort By: Total Followers | 7 Day Growth */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#ccc", fontWeight: 700 }}>Sort By:</span>
          <div
            role="tablist"
            aria-label="Sort followers by"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 6,
              background: "transparent",
              border: "1px solid #333",
              borderRadius: 999,
              padding: 4,
            }}
          >
            <button
              role="tab"
              aria-selected={followersSort === "followers"}
              onClick={() => setFollowersSort("followers")}
              style={{
                ...pillBase,
                background: followersSort === "followers" ? "#3fa9f5" : "transparent",
                color: followersSort === "followers" ? "#000" : "#ddd",
                whiteSpace: "nowrap",
              }}
            >
              Total Followers
            </button>
            <button
              role="tab"
              aria-selected={followersSort === "growth_7d"}
              onClick={() => setFollowersSort("growth_7d")}
              style={{
                ...pillBase,
                background: followersSort === "growth_7d" ? "#3fa9f5" : "transparent",
                color: followersSort === "growth_7d" ? "#000" : "#ddd",
                whiteSpace: "nowrap",
              }}
            >
              7 Day Growth
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onOpenFilters}
          title="Open filter on hover"
          aria-label="Open filter on hover"
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            border: "1px solid #555",
            background: "#111",
            color: "#dfe7ef",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.02)",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Steam() {
  const location = useLocation();
  const { currentUser } = useAuth();
  // Signed-in users without the steam permission get a locked public preview:
  // the top 5 card rows, with everything else veiled and tools disabled.
  // "?preview=public" forces the preview for any user (testing/demos); it only
  // ever restricts the view, never grants access.
  const forcePublicPreview = new URLSearchParams(location.search).get("preview") === "public";
  const isPublicPreview = forcePublicPreview || (Boolean(currentUser) && !currentUser.is_admin &&
    !(Array.isArray(currentUser.permissions) && currentUser.permissions.includes("steam")));
  const [previewMetrics, setPreviewMetrics] = useState(null);
  const previewWrapRef = useRef(null);
  const {
    metadata,
    latestCCU,
    loading: globalLoading,
    catalogLoading,
    catalogReady,
    ensureSteamCatalog,
    getAllTags,
    getAllPublishers,
    getAllDevelopers,
    getAllReleaseDates,
    refreshData,
  } = useGlobalData();

const [pendingChartRestore, setPendingChartRestore] = useState(null);

const restoringRef = useRef(false);
const restoreTokenRef = useRef(0);

  /** -----------------------
   *  Players (existing Steam)
   *  ----------------------- */
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameQuery, setGameQuery] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  // Primary Tags (first three tags only)
  const [selectedPrimaryTag, setSelectedPrimaryTag] = useState(null);
  const [primaryTagQuery, setPrimaryTagQuery] = useState("");
  const [displayedGames, setDisplayedGames] = useState([]);
  const [hiddenGames, setHiddenGames] = useState([]);
  const [steamTotalOnline, setSteamTotalOnline] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [loadingPercent, setLoadingPercent] = useState(4);

  // stream reset for effervescent
  const [streamNonce, setStreamNonce] = useState(0);
  const [popularFilter, setPopularFilter] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterCloseTimerRef = useRef(null);
  const [headerStatsTarget, setHeaderStatsTarget] = useState(null);
  const [marketShape, setMarketShape] = useState("squares");
  const [marketSelected, setMarketSelected] = useState(null);
  useEffect(() => {
    setHeaderStatsTarget(document.getElementById("steam-header-stats"));
  }, []);
  useEffect(() => {
    if (!filtersOpen) return;
    const close = event => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [filtersOpen]);
  useEffect(() => {
    if (isPublicPreview) setFiltersOpen(false);
  }, [isPublicPreview]);
  useEffect(() => {
    if (filtersOpen) return;
    const active = document.activeElement;
    if (document.querySelector(".steam-filter-workspace")?.contains(active)) active?.blur();
  }, [filtersOpen]);
  useEffect(() => {
    if (!marketSelected) return;
    const close = event => {
      if (event.key === "Escape") setMarketSelected(null);
    };
    const outside = event => {
      if (!event.target.closest(".steam-market-detail, .mm-node")) setMarketSelected(null);
    };
    window.addEventListener("keydown", close);
    document.addEventListener("click", outside);
    return () => {
      window.removeEventListener("keydown", close);
      document.removeEventListener("click", outside);
    };
  }, [marketSelected]);

  /** -----------------------
   *  Followers (moved here)
   *  ----------------------- */
  const [followersLoaded, setFollowersLoaded] = useState(false);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [mode, setMode] = useState("players"); // "players" | "followers"
  useEffect(() => {
    if (mode === "followers") ensureSteamCatalog();
  }, [mode, ensureSteamCatalog]);
  const activeFollowersLoading = mode === "followers" && (!catalogReady || followersLoading || catalogLoading);
  const steamLoading = globalLoading || loadingPlayers || activeFollowersLoading;
  const loadingTarget = globalLoading ? 28 : loadingPlayers ? 72 : activeFollowersLoading ? 94 : 100;

  useEffect(() => {
    if (!steamLoading) {
      setLoadingPercent(100);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setLoadingPercent((current) => {
        if (current >= loadingTarget) return current;
        const distance = loadingTarget - current;
        const step = Math.max(0.35, distance * 0.08);
        return Math.min(loadingTarget, current + step);
      });
    }, 120);
    return () => window.clearInterval(timer);
  }, [steamLoading, loadingTarget]);
  const [followersGames, setFollowersGames] = useState([]);
  const [followersDisplayed, setFollowersDisplayed] = useState([]);
  const [hiddenFollowers, setHiddenFollowers] = useState([]);
  const [followersSort, setFollowersSort] = useState("growth_7d"); // followers | growth_7d
  const [gamesFilter, setGamesFilter] = useState("all");
  const [followersValueMode, setFollowersValueMode] = useState("total"); // "total" | "daily"

  /* ============================
     Player History compare (live+daily)
     ============================ */
  const [chartGames, setChartGames] = useState([]); // [{ metadata, live, daily }]
  const [dataMode, setDataMode] = useState("live"); // "live" | "daily"
  const [viewByDaysSinceLaunch, setViewByDaysSinceLaunch] = useState(false);
  const [showRegionBands, setShowRegionBands] = useState(false);
  const [highlightedSeries, setHighlightedSeries] = useState(null); // app_id or null


  const [winStartIdx, setWinStartIdx] = useState(0);
  const [winEndIdx, setWinEndIdx] = useState(0);
  const [steamChartHeight, setSteamChartHeight] = useState(420);
  const hasInitWindow = useRef(false);

  const handleSteamChartResizeStart = (event) => {
    event.preventDefault();
    const startY = layoutCoordinate(event.clientY);
    const startHeight = steamChartHeight;
    const onMove = (moveEvent) => {
      setSteamChartHeight(Math.max(420, Math.min(900, startHeight + layoutCoordinate(moveEvent.clientY) - startY)));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  /* =========================
     Restore helpers (AFTER states)
     ========================= */

  const findGameStubForChart = (appId, targetMode) => {
    const idStr = String(appId);
    const list = targetMode === "followers" ? (followersGames || []) : (games || []);
    const hit = (list || []).find((g) => String(g.app_id) === idStr);

    if (hit) {
      return {
        app_id: idStr,
        name: String(hit.name || hit?.metadata?.name || idStr),
        box_art: String(hit.box_art || hit?.metadata?.box_art || ""),
        release_date: String(hit.release_date || hit?.metadata?.release_date || ""),
        full_price: String(hit.full_price || hit?.metadata?.full_price || ""),
      };
    }

    return { app_id: idStr, name: idStr, box_art: "", release_date: "", full_price: "" };
  };

  const loadSeriesForRestore = async (appId, targetMode) => {
    const idStr = String(appId);

    if (targetMode === "followers") {
      const daily = await loadFollowersSeries(idStr, 90);
      return { appId: idStr, live: [], daily };
    }

    const [live, daily] = await Promise.all([
      loadLiveSeries(idStr),
      loadDailySeries(idStr),
    ]);

    return { appId: idStr, live, daily };
  };

// ---- Market Mind precompute cache ----

  /* =============================
     NEW: Players vs Followers mode
     ============================= */
  // Switching tabs must not leak the other tab's search text into this one.
  const handleSetMode = (nextMode) => {
    setMode((prev) => {
      if (prev !== nextMode) setSearchTerm("");
      return nextMode;
    });
  };
  const [isReleasedThisWeek, setIsReleasedThisWeek] = useState(false);
  const [savedViewsKey, setSavedViewsKey] = useState(0); 

  /** Players view-type (unchanged) */
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "market" | "effervescent"

  /** Shared filters */
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);

  // ---- Publishers UI (lightweight) ----
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [pubQuery, setPubQuery] = useState(""); // user typing into publisher Select

  const [allTags, setAllTags] = useState([]);
  const [allPublishers, setAllPublishers] = useState([]);

  const [allDevelopers, setAllDevelopers] = useState([]);
  const [selectedDevelopers, setSelectedDevelopers] = useState(null);
  const [devQuery, setDevQuery] = useState("");

  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [releaseDateRange, setReleaseDateRange] = useState([null, null]);

  // Multi-select price tiers: array of tier ids (start with ALL selected)
  const [selectedPriceRange, setSelectedPriceRange] = useState(
    PRICE_TIERS.map((t) => t.id)
  );

  // ---------- Custom tooltip: sorted by CCU, UTC + PT time, comma formatting ----------
  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // For live mode, label is an ISO datetime (UTC)
    let headerLines = [];
    if (dataMode === "live") {
      const d = new Date(label);
      if (!Number.isNaN(d.getTime())) {
        const utcStr = d.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: "UTC",
        });
        const ptStr = d.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: "America/Los_Angeles",
        });
        headerLines = [
          `${utcStr} UTC`,
          `${ptStr} PT`,
        ];
      } else {
        headerLines = [String(label)];
      }
    } else {
  // Daily / Days-since-launch
  headerLines = viewByDaysSinceLaunch
  ? [String(label)]
  : [fmtDateShortUTC(label)];

}


    // Sort series by value DESC for that time point
    const sorted = [...payload]
      .filter((p) => p && Number.isFinite(Number(p.value)))
      .sort((a, b) => Number(b.value) - Number(a.value));

    const nf = new Intl.NumberFormat("en-US");

    return (
      <div
        style={{
          background: "#050608",
          border: "1px solid #333",
          borderRadius: 6,
          padding: "6px 10px 8px",
          fontSize: 12,
          color: "#f9fafb",
          minWidth: 180,
        }}
      >
        {/* Time header */}
        {headerLines.map((line, idx) => (
          <div
            key={idx}
            style={{
              fontWeight: idx === 0 ? 700 : 500,
              color: idx === 0 ? "#e5e7eb" : "#9ca3af",
            }}
          >
            {line}
          </div>
        ))}

        <div style={{ marginTop: 6 }}>
          {sorted.map((entry) => {
            const appId = entry.dataKey;
            // Find consistent series color
            const seriesIndex = chartGames.findIndex(
              (g) => String(g.metadata.app_id) === String(appId)
            );
            const color = seriesColor(seriesIndex >= 0 ? seriesIndex : 0);
            const name = entry.name || appId;
            const value = nf.format(Number(entry.value) || 0);

            return (
              <div
                key={appId}
                style={{
                  color,
                  fontWeight: 600,
                  marginBottom: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {name}: <span style={{ color: "#f9fafb" }}>{value}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

 
/** =========================
 * Hover preview like mindSHARE
 * ========================= */
const [preview, setPreview] = useState(null); // { type, value } or null

/** Persistent, click-to-pin highlights for Market Mind's filter dropdowns.
 * Unlike `preview` (transient, hover-driven), these stay lit until toggled
 * off, and multiple can be active at once (e.g. 3 tags + 2 publishers). */
const [pinnedHighlights, setPinnedHighlights] = useState([]); // [{type, value}]
const pinnedHighlightsRef = useRef([]);
useEffect(() => {
  pinnedHighlightsRef.current = pinnedHighlights;
  recomputeHighlightDim();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [pinnedHighlights]);

const toggleHighlightPin = useCallback((type, value) => {
  if (!type || !value) return;
  setPinnedHighlights((prev) => {
    const exists = prev.some((p) => p.type === type && p.value === value);
    return exists
      ? prev.filter((p) => !(p.type === type && p.value === value))
      : [...prev, { type, value }];
  });
}, []);

const isHighlightPinned = useCallback(
  (type, value) =>
    pinnedHighlights.some((p) => p.type === type && p.value === value),
  [pinnedHighlights]
);

const norm = (s = "") => s.toLowerCase().trim();

/** Reuse your normalized helpers for pubs/devs */
function matchesPreviewSteam(node, prev) {
  if (!prev || !prev.type || !prev.value) return true;
  const v = norm(prev.value);

  switch (prev.type) {
    case "tag": {
      const tags = Array.isArray(node.tags) ? node.tags.map(norm) : [];
      return tags.includes(v);
    }
        case "primaryTag": {
      const t = Array.isArray(node?.tags) ? node.tags.map(norm).slice(0, 5) : [];
      return t.includes(v);
    }
    case "publisher": {
      const pubs = getPublishersNorm(node); // already lowercased
      return pubs.includes(v);
    }
    case "developer": {
      const devs = getDevelopersNorm(node); // already lowercased
      return devs.includes(v);
    }
    case "game": {
      return node.app_id === String(prev.value) || norm(node.name) === v;
    }
    default:
      return true;
  }
}

  /** -------- Click-to-filter helpers from hover metadata -------- */

  // Price → pick the matching tier (or Free)
  const handleClickPriceFilter = (game) => {
    if (!game) return;

    // Free to Play
    if (game.f2p === true) {
      setSelectedPriceRange(["free"]);
      return;
    }

    const price = game.price_value;
    if (price == null || !Number.isFinite(price)) return;

    const tier = PRICE_TIERS.find(
      (t) => price >= t.min && price <= t.max
    );
    if (!tier) return;

    // Single-tier selection; slider will reflect it
    setSelectedPriceRange([tier.id]);
  };

  // Add a publisher into the multi-select filter
  const handleClickPublisherFilter = (name) => {
    if (!name) return;
    const value = name.trim();
    if (!value) return;

    setSelectedPublisher((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
      if (arr.some((p) => (p?.value || "") === value)) return arr;
      return [...arr, { label: value, value }];
    });
  };

  // Add a developer into the multi-select filter
  const handleClickDeveloperFilter = (name) => {
    if (!name) return;
    const value = name.trim();
    if (!value) return;

    setSelectedDevelopers((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
      if (arr.some((d) => (d?.value || "") === value)) return arr;
      return [...arr, { label: value, value }];
    });
  };

  // Add a tag into the Tags filter
  const handleClickTagFilter = (tag) => {
    if (!tag) return;
    const value = tag.trim();
    if (!value) return;

    setSelectedTag((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
      if (arr.some((t) => (t?.value || "") === value)) return arr;
      return [...arr, { label: value, value }];
    });
  };

/** Imperatively apply preview by toggling a CSS class on nodes */
const previewRef = useRef(null);
const previewRaf = useRef(0);
const clearTimerRef = useRef(0);

/** Dims every bubble that doesn't match at least one active highlight —
 * pinned (persistent, from the dropdown toggle) or hover (transient). Shows
 * everything (no dim) when neither is active. */
function recomputeHighlightDim() {
  if (!bubbleSvgRef.current) return;
  const list = pinnedHighlightsRef.current.concat(
    previewRef.current && previewRef.current.value ? [previewRef.current] : []
  );
  const sel = d3.select(bubbleSvgRef.current).selectAll(".mm-node");
  if (!list.length) {
    sel.classed("mm-bubble-dim", false);
    return;
  }
  sel.each(function () {
    const appId = this.getAttribute("data-app");
    let data = null;
    if (appId) data = nodeIndexRef.current.get(String(appId));
    const hit = list.some((p) => matchesPreviewSteam(data || {}, p));
    d3.select(this).classed("mm-bubble-dim", !hit);
  });
}

function applyPreview(prev) {
  // memoize latest request
  previewRef.current = prev;

  // if we’re asked to clear, do it with a tiny grace period to avoid flicker
  const wantsClear =
    !prev || !prev.type || !prev.value || (prev.type === "nameContains" && !String(prev.value).trim());

  if (wantsClear) {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      // only clear if nothing new arrived during the grace window
      if (!previewRef.current || !previewRef.current.value) {
        previewRef.current = null;
        recomputeHighlightDim();
      }
    }, 120); // small delay prevents “flash to all” when moving between items
    return;
  }

  // we have a real preview request: cancel pending clears and apply immediately
  if (clearTimerRef.current) {
    clearTimeout(clearTimerRef.current);
    clearTimerRef.current = 0;
  }

  if (previewRaf.current) return; // throttle to one per frame
  previewRaf.current = requestAnimationFrame(() => {
    previewRaf.current = 0;
    recomputeHighlightDim();
  });
}



// identical to Followers' pills
const pillBase = {
  border: "none",
  borderRadius: 999,
  padding: "8px 12px",
  fontWeight: 800,
  cursor: "pointer",
  transition: "background 0.15s ease",
};

  /** Build lists */
  useEffect(() => {
    if (globalLoading || !metadata.length) return;
    setAllTags(getAllTags());
    setAllPublishers(getAllPublishers());
    setAllDevelopers(getAllDevelopers());
    const r = getAllReleaseDates(); // expected [min, max] Dates (or strings)
    const hardMaxLocal = new Date(2030, 11, 31, 23, 59, 59, 999); // Dec=11
    if (Array.isArray(r) && r.length === 2) {
      const min = r[0] ? new Date(r[0]) : null;
      const max = r[1] ? new Date(r[1]) : null;
      setReleaseDateRange([min, (max && +max > +hardMaxLocal) ? max : hardMaxLocal]);
    } else {
      setReleaseDateRange([null, hardMaxLocal]);
    }

  }, [metadata, globalLoading, getAllTags, getAllPublishers, getAllDevelopers, getAllReleaseDates]);


useEffect(() => {
  const c = pendingChartRestore;
  if (!c) return;

  restoringRef.current = true;
  const myToken = ++restoreTokenRef.current;

  const ids = Array.isArray(c.ids) ? c.ids.map(String).filter(Boolean) : [];
  if (ids.length === 0) {
    setChartGames([]);
    setPendingChartRestore(null);

    if (restoreTokenRef.current === myToken) {
      restoringRef.current = false;
    }
    return;
  }

if (viewMode !== "cards") {
  if (restoreTokenRef.current === myToken) restoringRef.current = false;
  return;
}

  const targetMode = mode;

  // Wait for lists to exist (stay locked while waiting)
  if (targetMode === "followers" && (!followersGames || followersGames.length === 0)) return;
  if (targetMode !== "followers" && (!games || games.length === 0)) return;

  // apply chart toggles first
  if (targetMode === "followers") {
    setFollowersValueMode(c.followersValueMode ?? "total");
    setDataMode("daily");
    setViewByDaysSinceLaunch(false);
    setShowRegionBands(false);
  } else {
    setDataMode(c.dataMode ?? "live");
    setViewByDaysSinceLaunch(!!c.viewByDaysSinceLaunch);
    setShowRegionBands(!!c.showRegionBands);
  }
  setHighlightedSeries(c.highlightedSeries ?? null);

  // seed placeholders
  const stubs = ids.map((id) => findGameStubForChart(id, targetMode));
  setChartGames(
    stubs.map((m) => ({ metadata: m, live: [], daily: [], loading: true }))
  );
  hasInitWindow.current = false;

  let cancelled = false;

  (async () => {
    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          return await loadSeriesForRestore(id, targetMode);
        } catch (e) {
          console.warn("restore series failed:", id, e);
          return { appId: String(id), live: [], daily: [] };
        }
      })
    );

    if (cancelled) {
      if (restoreTokenRef.current === myToken) {
        restoringRef.current = false;
      }
      return;
    }

    setChartGames((prev) => {
      const byId = new Map(results.map((r) => [String(r.appId), r]));
      return prev.map((row) => {
        const id = String(row?.metadata?.app_id);
        const r = byId.get(id);
        if (!r) return { ...row, loading: false };
        return { ...row, live: r.live, daily: r.daily, loading: false };
      });
    });

    if (Number.isFinite(c.winStartIdx)) setWinStartIdx(c.winStartIdx);
    if (Number.isFinite(c.winEndIdx)) setWinEndIdx(c.winEndIdx);

    setPendingChartRestore(null);

    if (restoreTokenRef.current === myToken) {
      restoringRef.current = false;
    }
  })();

  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [pendingChartRestore, mode, viewMode, games, followersGames]);


  /** Players: process data */
  useEffect(() => {
    if (globalLoading || !metadata.length || !latestCCU.length) return;

    try {
      setLoadingPlayers(true);
      const metaMap = new Map();
      metadata.forEach((meta) => {
        const cleanAppId = String(parseInt(meta.app_id)).trim();
        if (cleanAppId && cleanAppId !== "0") metaMap.set(cleanAppId, meta);
      });

            const combined = latestCCU
        .map((ccu) => {
          const cleanAppId = String(parseInt(ccu.app_id)).trim();
          if (cleanAppId === "0") {
            setSteamTotalOnline(parseInt(ccu.ccu?.trim() || "0", 10));
            return null;
          }
          const meta = metaMap.get(cleanAppId);
          if (!meta) return null;

          const { priceValue, isF2P } = derivePrice(meta);

          return {
            app_id: cleanAppId,
            name: meta.name?.trim() || "",
            box_art: meta.box_art?.trim() || "",
            ccu: parseInt(ccu.ccu?.trim() || "0", 10),
            release_date: meta.release_date?.trim() || "",
            full_price: meta.full_price?.trim() || "",
            price_value: priceValue,     // numeric for filters
            f2p: isF2P,                  // boolean
            short_description: meta.short_description?.trim() || "",
            developers: meta.developers?.trim() || "",
            publishers: meta.publishers?.trim() || "",
            video: pickBestVideoUrl(meta),
            tags:
              meta.tags
                ?.split(",")
                .map((t) => t.trim())
                .filter(Boolean) || [],
            primary_tags:
              (meta.tags
                ?.split(",")
                .map((t) => t.trim())
                .filter(Boolean) || []
              ).slice(0, 5),
          };
        })

        .filter(Boolean);

      combined.sort((a, b) => b.ccu - a.ccu);
      setGames(combined);
      setDisplayedGames(combined);
    } catch (err) {
      console.error("❌ Failed to process game data:", err);
    } finally {
      setLoadingPlayers(false);
    }
  }, [metadata, latestCCU, globalLoading]);

// Prefetch Followers in background while user is on Players -> Cards
useEffect(() => {
  if (mode !== "players" || viewMode !== "cards") return;
  if (isPublicPreview) return; // preview users cannot open Followers; skip the gated files
  if (followersLoaded || followersLoading) return;
  if (!metadata.length || !catalogReady || catalogLoading) return;

  let cancelled = false;

  const run = async () => {
    try {
      // Give rendering time first; run when the browser is idle
      rIC(async () => {
        try {
          const [latestRows, growthRows] = await Promise.all([
            loadCsvGz(LATEST_FOLLOWERS_URL),
            loadCsvGz(FOLLOWERS_GROWTH_URL),
          ]);

          if (cancelled) return;

          const latestMap = new Map();
          for (const r of latestRows) {
            if (!r?.app_id) continue;
            const appId = Number(r.app_id);
            latestMap.set(appId, { followers: Number(r.followers) || 0, dt: r.dt || null });
          }

          const toNumOrNull = (v) =>
            v === null || v === undefined || v === "" ? null : Number(v);

          const growthMap = new Map();
          for (const r of growthRows) {
            if (!r?.app_id) continue;
            const appId = Number(r.app_id);
            growthMap.set(appId, {
              current_followers: Number(r.current_followers) || 0,
              growth_7d: toNumOrNull(r["7_day_growth"]),
            });
          }

          const metadataMap = {};
          metadata.forEach((m) => {
            if (m?.app_id) metadataMap[String(m.app_id).trim()] = m;
          });

          const excludedTypes = new Set([
            "episode","hardware","music","series","video","unlisted","not found",
          ]);

          const allAppIds = new Set([
            ...Object.keys(metadataMap),
            ...Array.from(latestMap.keys()),
            ...Array.from(growthMap.keys()),
          ]);

          const unified = [];
          for (const appId of allAppIds) {
  const meta = metadataMap[String(appId)];
  if (!meta) continue;

  const contentType = (meta.type || "").toLowerCase();
  if (excludedTypes.has(contentType)) continue;

  const latest = latestMap.get(appId);
  const growth = growthMap.get(appId);

  const followers =
    latest?.followers ?? null ?? growth?.current_followers ?? 0;
  if (followers == null) continue;      // keep 0, only drop null/undefined

  const g7raw = growth?.growth_7d;
  const computed7 =
    g7raw == null ? growth?.current_followers ?? followers ?? 0 : g7raw;

  // NEW: derive price + f2p for Followers as well
  const { priceValue, isF2P } = derivePrice(meta);

  unified.push({
    ...meta,
    app_id: String(appId),
    name: (meta.name || "").trim(),
    box_art: (meta.box_art || "").trim(),
    followers,
    growth_7d: computed7,
    tags: meta?.tags?.split(",").map((t) => t.trim()) || [],
    primary_tags:
      (meta?.tags?.split(",").map((t) => t.trim()) || []).slice(0, 5),
    video: pickBestVideoUrl(meta),
    publishers: meta.publishers || "",
    developers: meta.developers || "",
    short_description: meta.short_description || "",
    full_price: meta.full_price || "",
    price_value: priceValue,
    f2p: isF2P,
    release_date: meta.release_date || "",
  });
}


          if (cancelled) return;

          unified.sort((a, b) => (b.followers || 0) - (a.followers || 0));

          // Prime Followers state so toggle is instant
          setFollowersGames(unified);
          setFollowersDisplayed(unified);
          setFollowersLoaded(true);
        } catch (e) {
          // ignore background prefetch errors; normal on-toggle loader will still work
          // console.warn("Followers prefetch failed:", e);
        }
      });
    } catch {}
  };

  run();
  return () => { cancelled = true; };
}, [mode, viewMode, metadata, followersLoaded, followersLoading, catalogLoading, catalogReady]);

  /** Followers: load once upon entering Followers (when metadata ready) */
  useEffect(() => {
    if (mode !== "followers") return;
    if (followersLoaded || followersLoading) return;
    if (!metadata.length || !catalogReady || catalogLoading) return;

    const metadataMap = {};
    metadata.forEach((m) => {
      if (m?.app_id) metadataMap[String(m.app_id).trim()] = m;
    });

    const loadFollowers = async () => {
      setFollowersLoading(true);
      try {
        const [latestRows, growthRows] = await Promise.all([
          loadCsvGz(LATEST_FOLLOWERS_URL),
          loadCsvGz(FOLLOWERS_GROWTH_URL),
        ]);

        const latestMap = new Map();
        for (const r of latestRows) {
          if (!r?.app_id) continue;
          const appId = Number(r.app_id);
          latestMap.set(appId, {
            followers: Number(r.followers) || 0,
            dt: r.dt || null,
          });
        }

        const toNumOrNull = (v) =>
          v === null || v === undefined || v === "" ? null : Number(v);

        const growthMap = new Map();
        for (const r of growthRows) {
          if (!r?.app_id) continue;
          const appId = Number(r.app_id);
          growthMap.set(appId, {
            current_followers: Number(r.current_followers) || 0,
            growth_7d: toNumOrNull(r["7_day_growth"]),
          });
        }

        const allAppIds = new Set([
          ...Object.keys(metadataMap),
          ...Array.from(latestMap.keys()),
          ...Array.from(growthMap.keys()),
        ]);

        const excludedTypes = new Set([
          "episode",
          "hardware",
          "music",
          "series",
          "video",
          "unlisted",
          "not found",
        ]);

        const unified = [];
for (const appId of allAppIds) {
  const meta = metadataMap[String(appId)];
  if (!meta) continue;

  const contentType = (meta.type || "").toLowerCase();
  if (excludedTypes.has(contentType)) continue;

  const latest = latestMap.get(appId);
  const growth = growthMap.get(appId);

  const followers =
    latest?.followers ?? null ?? growth?.current_followers ?? 0;
  if (followers == null) continue; // keep 0, only drop null/undefined

  const g7raw = growth?.growth_7d;
  const computed7 =
    g7raw == null ? growth?.current_followers ?? followers ?? 0 : g7raw;

  // 🔹 FIX: actually derive price + f2p for Followers
  const { priceValue, isF2P } = derivePrice(meta);

  unified.push({
    ...meta,
    app_id: String(appId),
    name: (meta.name || "").trim(),
    box_art: (meta.box_art || "").trim(),
    followers,
    growth_7d: computed7,
    tags: meta?.tags?.split(",").map((t) => t.trim()) || [],
    primary_tags:
      (meta?.tags?.split(",").map((t) => t.trim()) || []).slice(0, 5),
    video: pickBestVideoUrl(meta),
    publishers: meta.publishers || "",
    developers: meta.developers || "",
    short_description: meta.short_description || "",
    full_price: meta.full_price || "",
    price_value: priceValue, // numeric for filters
    f2p: isF2P,              // boolean for "Free to Play"
    release_date: meta.release_date || "",
  });
}


        unified.sort((a, b) => (b.followers || 0) - (a.followers || 0));
        setFollowersGames(unified);
        setFollowersDisplayed(unified);
        setFollowersLoaded(true);
      } catch (e) {
        console.error("❌ Failed to load followers CSV.GZ:", e);
      } finally {
        setFollowersLoading(false);
      }
    };

    loadFollowers();
  }, [mode, metadata, followersLoaded, followersLoading, catalogLoading, catalogReady]);

  /** Shared export helper */
  const exportCSV = (rows, filename) => {
    const csv = Papa.unparse(rows);
    const utf8Bom = "\uFEFF";
    const blob = new Blob([utf8Bom + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Players handlers */
  const handleRemove = (app_id) => {
    if (isPublicPreview) return;
    setHiddenGames((prev) => [...prev, app_id]);
  };
  // Timestamp of the last Reset All click — used to ignore the default saved
  // view auto-apply that fires when SavedViewsManager remounts on reset.
  const lastResetAtRef = useRef(0);

  const handleResetPlayers = () => {
  lastResetAtRef.current = Date.now();
  setSavedViewsKey((k) => k + 1);
  setHiddenGames([]);
  setSelectedGame(null);
  setSelectedTag(null);
  setSelectedPrimaryTag(null);
  setSelectedPublisher(null);
  setSelectedDevelopers(null);
  setGameQuery("");
  setTagQuery("");
  setPrimaryTagQuery("");
  setPubQuery("");
  setDevQuery("");
  setSearchTerm("");
  setDisplayedGames(games);
  setStreamNonce((n) => n + 1);
  setHoveredCards({});
  setPopularFilter(null);
  setIsReleasedThisWeek(false);
  setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
  setSelectedDateRange([null, null]);
  setChartGames([]);
  setHighlightedSeries(null);
  setWinStartIdx(0);
  setWinEndIdx(0);
  setDataMode("live");
  setViewByDaysSinceLaunch(false);
  setShowRegionBands(false);
  setFollowersValueMode("total");
  setGamesFilter("all");
  setPinnedHighlights([]);
  setPreview(null);
  previewRef.current = null;
  if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
  clearTimerRef.current = 0;
  if (previewRaf.current) cancelAnimationFrame(previewRaf.current);
  previewRaf.current = 0;
  hasInitWindow.current = false;
};

  const handleExportPlayers = () => {
    const visibleGames = filteredPlayers.filter(
      (g) => !hiddenGames.includes(g.app_id)
    );
    exportCSV(visibleGames, "Steam_Live_Player_Counts.csv");
  };
  const handleReleasedThisWeekPlayers = () => {
    const filtered = games.filter((g) => withinLastNDays(g.release_date, 7));
    setDisplayedGames(filtered);
    setStreamNonce((n) => n + 1);
  };

  const applyPopularFilter = (filter) => {
    const year = new Date().getFullYear();
    // Filter each mode's own dataset — never cross Players/Followers objects.
    const source = mode === "players" ? games : followersGames;
    let next = source;
    if (filter === "week") next = source.filter((g) => withinLastNDays(g.release_date, 7));
    if (filter === "year") next = source.filter((g) => new Date(g.release_date).getFullYear() === year);
    if (filter === "free") next = source.filter((g) => g.f2p === true || g.price_value === 0);
    setPopularFilter(filter);
    if (mode === "players") {
      setDisplayedGames(next);
    } else {
      setFollowersDisplayed(next);
    }
    setIsReleasedThisWeek(filter === "week");
    if (filter === "free") setSelectedPriceRange(["free"]);
  };

 // Quick refresh button: re-download latest_ccu.csv.gz and recompute games in-place
const refreshSteamData = useCallback(async () => {
  try {
    // 1) Fetch latest_ccu.csv.gz with a cache-buster
    const rows = await loadCsvGz(`${LATEST_CCU_URL}?t=${Date.now()}`);

    // 2) Build metadata map (same as in Players useEffect)
    const metaMap = new Map();
    metadata.forEach((meta) => {
      const cleanAppId = String(parseInt(meta.app_id)).trim();
      if (cleanAppId && cleanAppId !== "0") metaMap.set(cleanAppId, meta);
    });

    // 3) Rebuild combined games + total online
    let newOnline = 0;

    const combined = rows
      .map((ccu) => {
        const cleanAppId = String(parseInt(ccu.app_id)).trim();
        if (!cleanAppId) return null;

        if (cleanAppId === "0") {
          const val =
            typeof ccu.ccu === "number"
              ? ccu.ccu
              : parseInt(String(ccu.ccu || "0").trim(), 10);
          newOnline = Number.isFinite(val) ? val : 0;
          return null;
        }

        const meta = metaMap.get(cleanAppId);
        if (!meta) return null;

        const ccuVal =
          typeof ccu.ccu === "number"
            ? ccu.ccu
            : parseInt(String(ccu.ccu || "0").trim(), 10);

        const { priceValue, isF2P } = derivePrice(meta);

        return {
          app_id: cleanAppId,
          name: meta.name?.trim() || "",
          box_art: meta.box_art?.trim() || "",
          ccu: Number.isFinite(ccuVal) ? ccuVal : 0,
          release_date: meta.release_date?.trim() || "",
          full_price: meta.full_price?.trim() || "",
          price_value: priceValue,
          f2p: isF2P,
          short_description: meta.short_description?.trim() || "",
          developers: meta.developers?.trim() || "",
          publishers: meta.publishers?.trim() || "",
          video: pickBestVideoUrl(meta),
          tags: meta.tags?.split(",").map((t) => t.trim()).filter(Boolean) || [],
          primary_tags:
            (meta.tags?.split(",").map((t) => t.trim()).filter(Boolean) || []).slice(0, 5),
        };
      })
      .filter(Boolean);

    combined.sort((a, b) => b.ccu - a.ccu);

    setSteamTotalOnline(newOnline);
    setGames(combined);
    setDisplayedGames(combined);

    // Refresh any games in chart
    if (chartGames.length) {
      const refreshed = await Promise.all(
        chartGames.map(async (g) => {
          const appId = String(g.metadata.app_id);
          const [live, daily] = await Promise.all([
            loadLiveSeries(appId),
            loadDailySeries(appId),
          ]);
          return { ...g, live, daily };
        })
      );
      hasInitWindow.current = false;
      setChartGames(refreshed);
    }
  } catch (err) {
    console.error("Steam refresh failed:", err);
  }
}, [metadata, chartGames]); // ✅ THIS LINE WAS MISSING IN YOUR VERSION

const handleQuickRefresh = async () => {
  armRefreshCooldown(); // ✅ ONLY manual click triggers cooldown
  await refreshSteamData();
};


// Price formatter: uses derived price + f2p flag
const formatPrice = (game) => {
  if (!game) return null;

  // Only treat **boolean true** as free-to-play
  if (game.f2p === true) return "Free to Play";

  const n = game.price_value;
  if (n == null || !Number.isFinite(n)) {
    // No known price
    return "-";
  }

  const formatted = Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // NO SPACE after $
  return `$${formatted}`;
};



    /** Followers handlers */
  const handleRemoveFollower = (app_id) =>
    setHiddenFollowers((prev) => [...prev, app_id]);

  const handleResetFollowers = () => {
    lastResetAtRef.current = Date.now();
    setSavedViewsKey((k) => k + 1);
    setHiddenFollowers([]);
    setSelectedGame(null);
    setSelectedTag(null);
    setSelectedPrimaryTag(null);
    setSelectedPublisher(null);
    setSelectedDevelopers(null);
    setGameQuery("");
    setTagQuery("");
    setPrimaryTagQuery("");
    setPubQuery("");
    setDevQuery("");
    setSearchTerm("");
    setFollowersDisplayed(followersGames);
    setHoveredCards({});
    setIsReleasedThisWeek(false);
    setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
    setSelectedDateRange([null, null]);
    setPopularFilter(null);
    setGamesFilter("all");

    // 🔹 Reset the compare chart, same as Players
    setChartGames([]);
    setHighlightedSeries(null);
    setWinStartIdx(0);
    setWinEndIdx(0);
    setDataMode("daily");
    setViewByDaysSinceLaunch(false);
    setShowRegionBands(false);
    setFollowersValueMode("total");
    setPinnedHighlights([]);
    setPreview(null);
    previewRef.current = null;
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = 0;
    if (previewRaf.current) cancelAnimationFrame(previewRaf.current);
    previewRaf.current = 0;
    hasInitWindow.current = false;
  };


  const handleExportFollowers = () => {
    const visible = filteredFollowers.filter(
      (g) => !hiddenFollowers.includes(g.app_id)
    );
    exportCSV(visible, "Steam_Followers.csv");
  };
  const handleReleasedThisWeekFollowers = () => {
    const filtered = followersGames.filter((g) =>
      withinLastNDays(g.release_date, 7)
    );
    setFollowersDisplayed(filtered);
  };

// Prefer microtrailer if available, otherwise fall back to main trailer/video
function pickBestVideoUrl(meta) {
  if (!meta) return "";
  return (
    (meta.microtrailer ||
      meta.micro_trailer ||
      meta.micro_trailer_url ||
      meta.video ||
      meta.trailer ||
      meta.video_url ||
      "") + ""
  ).trim();
}

  /* ============================
     FAST PUBLISHER FILTER PATH
     ============================ */

  // --- Helpers to safely split publisher strings without creating stray "Inc." entries ---
  function normalizePublisherCommas(raw = "") {
    // remove the comma before common suffixes so "Studio, Inc." -> "Studio Inc."
    return raw.replace(
      /,\s*(inc\.?|ltd\.?|llc\.?|corp\.?|corporation|co\.?(?:,\s*ltd\.?)?|gmbh|plc|s\.a\.|s\.r\.l\.|pte\.?\s*ltd\.?|k\.k\.)\b/gi,
      " $1"
    );
  }
  function splitPublishersSafe(raw = "") {
    const cleaned = normalizePublisherCommas(raw);
    return cleaned
      .split(/[,;|/]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Per-game normalized publisher cache: app_id -> string[] (lowercased)
  const pubNormCache = useRef(new Map());
  function getPublishersNorm(g) {
    const key = g.app_id;
    const cached = pubNormCache.current.get(key);
    if (cached) return cached;
    const list = splitPublishersSafe(g.publishers || g.publisher || "").map(
      (s) => s.toLowerCase()
    );
    pubNormCache.current.set(key, list);
    return list;
  }

  // Optional global index (lazy): publisherLower -> Set<app_id>
  const publisherIndexRef = useRef(null);
  function buildPublisherIndexIfNeeded(sourceGames) {
    if (publisherIndexRef.current) return;
    const idx = new Map();
    for (const g of sourceGames) {
      const pubs = getPublishersNorm(g);
      for (const p of pubs) {
        if (!idx.has(p)) idx.set(p, new Set());
        idx.get(p).add(g.app_id);
      }
    }
    publisherIndexRef.current = idx;
  }

  // Build the index when the menu opens, in idle time (no impact to TTI)
  const onPublishersMenuOpen = () => {
    const doBuild = () =>
      buildPublisherIndexIfNeeded([...games, ...followersGames]);
    (window.requestIdleCallback || ((cb) => setTimeout(cb, 0)))(doBuild);
  };

  // Lightweight options list: cap count, only expand when user types
  const publisherOptions = useMemo(() => {
    const MAX_DEFAULT = 200;
    const MAX_FILTERED = 500;
    if (!allPublishers?.length) return [];
    if ((pubQuery || "").length < 2) {
      return allPublishers
        .slice(0, MAX_DEFAULT)
        .map((p) => ({ label: p, value: p }));
    }
    const q = pubQuery.toLowerCase();
    return allPublishers
      .filter((p) => p.toLowerCase().includes(q))
      .slice(0, MAX_FILTERED)
      .map((p) => ({ label: p, value: p }));
  }, [allPublishers, pubQuery]);

  // Top publishers by current CCU (players), built once games are loaded — uses safe split
  const TOP_PUBLISHERS_LIMIT = 500;
  const publisherOptionsByCCU = useMemo(() => {
    if (!games?.length) {
      return allPublishers.map((p) => ({ label: p, value: p }));
    }

    // accumulate using lowercase key, but keep the richest display name variant
    const acc = new Map(); // keyLower -> { name, total, count }
    for (const g of games) {
      const pubs = splitPublishersSafe(g.publishers || "");
      for (const name of pubs) {
        const key = name.toLowerCase();
        const prev = acc.get(key) || { name, total: 0, count: 0 };
        const total = prev.total + (g.ccu || 0);
        const count = prev.count + 1;
        // prefer longer display variant if we see multiple
        const display = name.length > prev.name.length ? name : prev.name;
        acc.set(key, { name: display, total, count });
      }
    }

    return Array.from(acc.values())
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
      .slice(0, TOP_PUBLISHERS_LIMIT)
      .map(({ name, total, count }) => ({
        value: name,
        label: `${name} — ${total.toLocaleString()} CCU • ${count} game${
          count === 1 ? "" : "s"
        }`,
      }));
  }, [games, allPublishers]);

  // Top tags by current CCU (players), same idea as publishers
  const TOP_TAGS_LIMIT = 500;
  const tagsOptionsByCCU = useMemo(() => {
    if (!games?.length) {
      return allTags.map((t) => ({ label: t, value: t }));
    }
    const acc = new Map(); // tag -> { total, count }
    for (const g of games) {
      const tags = Array.isArray(g.tags) ? g.tags : [];
      for (const t of tags) {
        const prev = acc.get(t) || { total: 0, count: 0 };
        acc.set(t, { total: prev.total + (g.ccu || 0), count: prev.count + 1 });
      }
    }
    return Array.from(acc.entries())
      .sort((a, b) => b[1].total - a[1].total || a[0].localeCompare(b[0]))
      .slice(0, TOP_TAGS_LIMIT)
      .map(([tag, { total, count }]) => ({
        value: tag,
        label: `${tag} — ${total.toLocaleString()} CCU • ${count} game${
          count === 1 ? "" : "s"
        }`,
      }));
  }, [games, allTags]);

// Alphabetized Primary Tags (first 5 tags from metadata only)
const primaryTagsAlphaOptions = useMemo(() => {
  if (!metadata?.length) return [];
  const set = new Set();
  for (const m of metadata) {
    const t = (m?.tags || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 5);
    for (const tag of t) set.add(tag);
  }
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map((tag) => ({ label: tag, value: tag }));
}, [metadata]);


  const gamesOptionsByCCU = useMemo(() => {
    const totalCcu = games.reduce((sum, g) => sum + (g.ccu || 0), 0);
    return games
      .slice() // avoid mutating original array
      .sort((a, b) => b.ccu - a.ccu)
      .map((g) => {
        const share =
          totalCcu > 0 ? ((g.ccu / totalCcu) * 100).toFixed(2) : "0.00";
        return {
          label: `${g.name} — ${g.ccu?.toLocaleString()} CCU • ${share}%`,
          value: g.app_id,
        };
      });
  }, [games]);

  /** -------- Filtering (Developers) -------- */
  function normalizeDeveloperCommas(raw = "") {
    return raw.replace(
      /,\s*(inc\.?|ltd\.?|llc\.?|corp\.?|corporation|co\.?(?:,\s*ltd\.?)?|gmbh|plc|s\.a\.|s\.r\.l\.|pte\.?\s*ltd\.?|k\.k\.)\b/gi,
      " $1"
    );
  }

  function splitDevelopersSafe(raw = "") {
    const cleaned = normalizeDeveloperCommas(raw);
    return cleaned
      .split(/[,;|/]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const devNormCache = useRef(new Map());
  function getDevelopersNorm(g) {
    const key = g.app_id;
    const cached = devNormCache.current.get(key);
    if (cached) return cached;
    const list = splitDevelopersSafe(g.developers || g.developer || "").map(
      (s) => s.toLowerCase()
    );
    devNormCache.current.set(key, list);
    return list;
  }

  const developerOptions = useMemo(() => {
    if (!allDevelopers?.length) return [];
    const filtered = devQuery
      ? allDevelopers.filter((d) =>
          d.toLowerCase().includes(devQuery.toLowerCase())
        )
      : allDevelopers;
    return filtered.map((d) => ({ label: d, value: d }));
  }, [allDevelopers, devQuery]);

  const developerOptionsByCCU = useMemo(() => {
    if (!games?.length) {
      return allDevelopers.map((d) => ({ label: d, value: d }));
    }

    const acc = new Map();
    for (const g of games) {
      const devs = splitDevelopersSafe(g.developers || "");
      for (const name of devs) {
        const key = name.toLowerCase();
        const prev = acc.get(key) || { name, total: 0, count: 0 };
        const total = prev.total + (g.ccu || 0);
        const count = prev.count + 1;
        const display = name.length > prev.name.length ? name : prev.name;
        acc.set(key, { name: display, total, count });
      }
    }

    return Array.from(acc.values())
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
      .map(({ name, total, count }) => ({
        value: name,
        label: `${name} — ${total.toLocaleString()} CCU • ${count} game${
          count === 1 ? "" : "s"
        }`,
      }));
  }, [games, allDevelopers]);

  /** -------- Filtering (Players) -------- */
  const filteredPlayers = useMemo(() => {
    let result = displayedGames
      .filter((g) => !hiddenGames.includes(g.app_id))
      .filter((g) => nameMatchesSearch(g.name, searchTerm))
      .filter((g) => {
  if (!selectedTag) return true;
  const sel = Array.isArray(selectedTag) ? selectedTag : [selectedTag];
  if (sel.length === 0) return true;
  const tags = Array.isArray(g.tags) ? g.tags : [];
  return sel.some((t) => tags.includes(t.value)); // OR across selected tags
})
// Primary Tags (first five only)
.filter((g) => {
  if (!selectedPrimaryTag) return true;
  const sel = Array.isArray(selectedPrimaryTag) ? selectedPrimaryTag : [selectedPrimaryTag];
  if (sel.length === 0) return true;
  const p = Array.isArray(g.primary_tags) ? g.primary_tags : (Array.isArray(g.tags) ? g.tags.slice(0, 5) : []);
  return sel.some((t) => p.includes(t.value));
})

      .filter((g) => {
        if (!selectedGame) return true;
        if (Array.isArray(selectedGame)) {
          return selectedGame.some((sel) => g.app_id === sel.value);
        }
        return g.app_id === selectedGame.value;
      });

    if (selectedPublisher) {
  const sel = Array.isArray(selectedPublisher) ? selectedPublisher : [selectedPublisher];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    const idx = publisherIndexRef.current;
    if (idx) {
      // fast path via index: OR across the chosen publishers
      result = result.filter((g) => {
        const idStr = g.app_id;
        for (const w of wants) {
          const set = idx.get(w);
          if (set && set.has(idStr)) return true;
        }
        return false;
      });
    } else {
      // fallback: per-card normalized list
      result = result.filter((g) => {
        const pubs = getPublishersNorm(g); // lowercased array
        return wants.some((w) => pubs.includes(w));
      });
    }
  }
}

    if (selectedDevelopers) {
  const sel = Array.isArray(selectedDevelopers) ? selectedDevelopers : [selectedDevelopers];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    result = result.filter((g) => {
      const devs = getDevelopersNorm(g); // lowercased array
      return wants.some((w) => devs.includes(w)); // OR across selected devs
    });
  }
}

    if (selectedDateRange[0] && selectedDateRange[1]) {
      const [from, to] = selectedDateRange;
      result = result.filter((g) => {
        if (!g.release_date) return false;
        const rd = new Date(g.release_date);
        return rd >= from && rd <= to;
      });
    }

            const activeTierIds = Array.isArray(selectedPriceRange)
      ? selectedPriceRange
      : [];

    if (activeTierIds.length > 0 && activeTierIds.length < PRICE_TIERS.length) {
      result = result.filter((g) => {
        const price = g.price_value;
        if (price == null || !Number.isFinite(price)) return false; // unknown price -> excluded when filtering
        return activeTierIds.some((id) => {
          const tier = PRICE_TIER_MAP[id];
          if (!tier) return false;
          return price >= tier.min && price <= tier.max;
        });
      });
    }


    // Only enforce CCU>0 when no explicit publisher is selected
    if (!selectedPublisher) {
      result = result.filter((g) => g.ccu > 0);
    }

    return result;
  }, [
    displayedGames,
    hiddenGames,
    searchTerm,
    selectedGame,
    selectedTag,
    selectedPublisher,
    selectedDevelopers,
    selectedDateRange,
    selectedPriceRange,
    selectedPrimaryTag,
  ]);

  /** -------- Filtering (Followers) -------- */
  const filteredFollowers = useMemo(() => {
    let result = followersDisplayed
      .filter((g) => !hiddenFollowers.includes(g.app_id))
      .filter((g) => nameMatchesSearch(g.name, searchTerm))
      .filter((g) => {
  if (!selectedTag) return true;
  const sel = Array.isArray(selectedTag) ? selectedTag : [selectedTag];
  if (sel.length === 0) return true;
  const tags = Array.isArray(g.tags) ? g.tags : [];
  return sel.some((t) => tags.includes(t.value));
})
// Primary Tags (first three only)
.filter((g) => {
  if (!selectedPrimaryTag) return true;
  const sel = Array.isArray(selectedPrimaryTag) ? selectedPrimaryTag : [selectedPrimaryTag];
  if (sel.length === 0) return true;
  const p = Array.isArray(g.primary_tags) ? g.primary_tags : (Array.isArray(g.tags) ? g.tags.slice(0, 5) : []);
  return sel.some((t) => p.includes(t.value));
})

      .filter((g) => {
        if (!selectedGame) return true;
        if (Array.isArray(selectedGame)) {
          return selectedGame.some((sel) => g.app_id === sel.value);
        }
        return g.app_id === selectedGame.value;
      })
      .filter((g) => {
        if (gamesFilter !== "unreleased") return true;

        const rd = safeDate(g.release_date);

        // ✅ Safer rule: Unreleased must have a real future date.
// Missing/invalid date -> EXCLUDE from Unreleased
if (!rd) return false;

// Future date -> Unreleased
return rd > new Date();

      });

    if (selectedPublisher) {
  const sel = Array.isArray(selectedPublisher) ? selectedPublisher : [selectedPublisher];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    const idx = publisherIndexRef.current;
    if (idx) {
      // fast path via index: OR across the chosen publishers
      result = result.filter((g) => {
        const idStr = g.app_id;
        for (const w of wants) {
          const set = idx.get(w);
          if (set && set.has(idStr)) return true;
        }
        return false;
      });
    } else {
      // fallback: per-card normalized list
      result = result.filter((g) => {
        const pubs = getPublishersNorm(g); // lowercased array
        return wants.some((w) => pubs.includes(w));
      });
    }
  }
}

            const activeTierIds = Array.isArray(selectedPriceRange)
      ? selectedPriceRange
      : [];

    if (activeTierIds.length > 0 && activeTierIds.length < PRICE_TIERS.length) {
      result = result.filter((g) => {
        const price = g.price_value;
        if (price == null || !Number.isFinite(price)) return false;
        return activeTierIds.some((id) => {
          const tier = PRICE_TIER_MAP[id];
          if (!tier) return false;
          return price >= tier.min && price <= tier.max;
        });
      });
    }



    if (selectedDateRange[0] && selectedDateRange[1]) {
      const [from, to] = selectedDateRange;
      result = result.filter((g) => {
        if (!g.release_date) return false;
        const rd = new Date(g.release_date);
        return rd >= from && rd <= to;
      });
    }

    if (selectedDevelopers) {
  const sel = Array.isArray(selectedDevelopers) ? selectedDevelopers : [selectedDevelopers];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    result = result.filter((g) => {
      const devs = getDevelopersNorm(g); // lowercased array
      return wants.some((w) => devs.includes(w)); // OR across selected devs
    });
  }
}

    return result;
  }, [
    followersDisplayed,
    hiddenFollowers,
    searchTerm,
    selectedGame,
    selectedTag,
    selectedPublisher,
    selectedDevelopers,
    gamesFilter,
    selectedDateRange,
    selectedPriceRange,
    selectedPrimaryTag,
  ]);

  /** Sorting (Followers) */
  const sortedFollowers = useMemo(() => {
    const copy = [...filteredFollowers];
    if (followersSort === "growth_7d") {
      copy.sort(
        (a, b) => (b.growth_7d ?? -Infinity) - (a.growth_7d ?? -Infinity)
      );
    } else {
      copy.sort((a, b) => (b.followers || 0) - (a.followers || 0));
    }
    return copy;
  }, [filteredFollowers, followersSort]);

  /** Stats */
  const { totalCcu } = useMemo(() => {
    const total = filteredPlayers.reduce((sum, g) => sum + (g.ccu || 0), 0);
    return { totalCcu: total };
  }, [filteredPlayers]);

  const baselineTotalCcu = useMemo(() => {
    return games.reduce((sum, g) => {
      if (!(g.ccu > 0)) return sum;
      return sum + (g.ccu || 0);
    }, 0);
  }, [games]);

  const playersShareOfTotal = useMemo(() => {
    if (baselineTotalCcu <= 0) return "0.00";
    return ((totalCcu / baselineTotalCcu) * 100).toFixed(2);
  }, [totalCcu, baselineTotalCcu]);

// When games are added to the Player History chart, compute stats for that selection
const chartSelectionStats = useMemo(() => {
  if (!chartGames.length) return null;

  const ids = new Set(chartGames.map((g) => String(g.metadata.app_id)));

  let totalChartCcu = 0;
  for (const g of games) {
    if (ids.has(String(g.app_id))) {
      totalChartCcu += g.ccu || 0;
    }
  }

  const pctOfBaseline =
    baselineTotalCcu > 0
      ? ((totalChartCcu / baselineTotalCcu) * 100).toFixed(2)
      : "0.00";

  return {
    totalChartCcu,
    pctOfBaseline,
  };
}, [chartGames, games, baselineTotalCcu]);

// Are we in the compare chart mode (Players + Cards + at least one chart game)?
const inChartMode =
  mode === "players" && viewMode === "cards" && !!chartSelectionStats;

// Header numbers: total CCU + % of baseline
const headerTotalCcu =
  inChartMode && chartSelectionStats
    ? chartSelectionStats.totalChartCcu
    : totalCcu;

const headerPctRaw =
  inChartMode && chartSelectionStats
    ? chartSelectionStats.pctOfBaseline
    : playersShareOfTotal;

// If it's exactly 100.00, display 100; otherwise keep XX.XX
const headerPlayersShareDisplay =
  headerPctRaw === "100.00" ? "100" : headerPctRaw;
const initialDashboardLoading =
  steamLoading && !(mode === "players" ? games.length : followersGames.length);

    const followersTotal = useMemo(
    () => sortedFollowers.reduce((sum, g) => sum + (g.followers || 0), 0),
    [sortedFollowers]
  );

  // When games are added to the Followers chart, compute total followers
  const followersChartStats = useMemo(() => {
    if (mode !== "followers" || !chartGames.length) return null;

    const ids = new Set(chartGames.map((g) => String(g.metadata.app_id)));

    let totalFollowers = 0;
    for (const g of followersGames) {
      if (ids.has(String(g.app_id))) {
        totalFollowers += g.followers || 0;
      }
    }

    return { totalFollowers };
  }, [mode, chartGames, followersGames]);

  const inFollowersChartMode =
    mode === "followers" && viewMode === "cards" && !!followersChartStats;

  const headerFollowersTotal =
    inFollowersChartMode && followersChartStats
      ? followersChartStats.totalFollowers
      : followersTotal;


  /** Windowed rendering (Players cards) */
  const INITIAL_BATCH = 120;
  const BATCH_SIZE = 80;
  const [itemsShown, setItemsShown] = useState(INITIAL_BATCH);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setItemsShown(INITIAL_BATCH);
  }, [
    searchTerm,
    selectedGame,
    selectedTag,
    selectedPublisher,
    displayedGames,
    hiddenGames,
    loadingPlayers,
    viewMode,
    mode,
  ]);

  // Effervescent sizing (no scroll)
  const mainRef = useRef(null);
  const headerRef = useRef(null);
  const [effHeight, setEffHeight] = useState(600);

  useEffect(() => {
    const calc = () => {
      const mainEl = mainRef.current;
      const headerEl = headerRef.current;
      if (!mainEl || !headerEl) return; // guard inside callback
      const mainRect = layoutRect(mainEl);
      const headerH = layoutRect(headerEl).height;
      setEffHeight(Math.max(0, mainRect.height - headerH));
    };
    const ro = new ResizeObserver(calc);
    if (mainRef.current) ro.observe(mainRef.current);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener("resize", calc);
    calc();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calc);
    };
  }, []);

  useEffect(() => {
    if (mode !== "players" || viewMode !== "cards") return;
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setItemsShown((prev) =>
            prev >= filteredPlayers.length
              ? prev
              : Math.min(filteredPlayers.length, prev + BATCH_SIZE)
          );
        }
      },
      { root: null, rootMargin: "800px 0px 800px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [filteredPlayers.length, viewMode, mode]);

  const visiblePlayers = useMemo(
    () =>
      viewMode === "cards"
        ? filteredPlayers.slice(0, itemsShown)
        : filteredPlayers,
    [filteredPlayers, itemsShown, viewMode]
  );

  const hasMorePlayers =
    mode === "players" &&
    viewMode === "cards" &&
    itemsShown < filteredPlayers.length;

  // Public preview: measure the rendered card grid to show 5 full rows, then
  // veil 2 ghost rows beneath them (same pattern as the mindSHARE preview).
  useLayoutEffect(() => {
    if (!isPublicPreview || mode !== "players" || viewMode !== "cards") return undefined;
    const wrap = previewWrapRef.current;
    const grid = wrap?.querySelector("[data-steam-grid]");
    if (!wrap || !grid) return undefined;
    const measure = () => {
      const cards = grid.querySelectorAll("[data-steam-card]");
      if (!cards.length) return;
      const first = cards[0].getBoundingClientRect();
      const gridBox = grid.getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(grid).rowGap) || 12;
      const rowHeight = first.height + gap;
      const firstTop = first.top;
      let perRow = 0;
      for (const card of cards) {
        if (Math.abs(card.getBoundingClientRect().top - firstTop) < 2) perRow += 1;
        else break;
      }
      perRow = Math.max(1, perRow);
      const visibleCount = 5 * perRow;
      const totalCount = Math.min(visiblePlayers.length, visibleCount + 2 * perRow);
      // Veil starts in the middle of the gap below row 5.
      const veilTop = (first.top - gridBox.top) + 5 * rowHeight - gap / 2;
      const minHeight = veilTop + 2 * rowHeight;
      const ctaTop = veilTop + rowHeight;
      setPreviewMetrics((previous) => (
        !previous || Math.abs(previous.veilTop - veilTop) > 1 || Math.abs(previous.minHeight - minHeight) > 1 ||
        previous.visibleCount !== visibleCount || previous.totalCount !== totalCount
          ? { veilTop, ctaTop, minHeight, visibleCount, totalCount }
          : previous
      ));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [isPublicPreview, mode, viewMode, visiblePlayers.length, globalLoading]);

  // ===== Market/Effervescent scaffolding (Players only) =====
  const bubbleContainerRef = useRef(null);
  const bubbleSvgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [zoomT, setZoomT] = useState(d3.zoomIdentity);
  const [bubbleHovered, setBubbleHovered] = useState(null);

  useEffect(() => {
    if (mode !== "players" || !mainRef.current) return;
    const el = mainRef.current;
    const measure = () => {
      const rect = layoutRect(el);
      const next = steamMarketDimensions(rect.width, layoutViewportHeight(), rect.top);
      setDimensions(previous => previous.width === next.width && previous.height === next.height ? previous : next);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const header = document.querySelector(".mg-app-header");
    if (header) ro.observe(header);
    window.addEventListener("resize", measure);
    measure();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [mode]);

  const { maxCcu } = useMemo(() => {
    const max = Math.max(1, ...filteredPlayers.map((g) => g.ccu || 0));
    return { maxCcu: max };
  }, [filteredPlayers]);

  const colorScale = useMemo(() => {
    const palette = [
      MARKET_PALETTE.declineWorst,
      MARKET_PALETTE.coral,
      MARKET_PALETTE.softCoral,
      MARKET_PALETTE.warmWhite,
      MARKET_PALETTE.slate,
      MARKET_PALETTE.growthLow,
      MARKET_PALETTE.growth,
      MARKET_PALETTE.growthPeak,
    ];
    const interp = d3.piecewise(d3.interpolateRgb.gamma(2.2), palette);

    const LOW_RED_CUTOFF = 100;
    const max = Math.max(LOW_RED_CUTOFF * 10, maxCcu || 1);

    const logT = d3
      .scaleLog()
      .domain([LOW_RED_CUTOFF, max])
      .range([0.25, 1])
      .clamp(true);

    return (ccu) => {
      const v = Math.max(0, ccu || 0);
      if (v === 0) return palette[0];
      if (v < LOW_RED_CUTOFF) {
        return interp((v / LOW_RED_CUTOFF) * 0.25);
      }
      return interp(logT(v));
    };
  }, [maxCcu]);

  const [marketLayout, setMarketLayout] = useState(null);
  const [marketLayoutRetry, setMarketLayoutRetry] = useState(0);
  const marketLayoutMatches = marketLayout?.games === filteredPlayers &&
    marketLayout?.width === dimensions.width && marketLayout?.height === dimensions.height &&
    marketLayout?.colorScale === colorScale && marketLayout?.shape === marketShape;
  const bubbleNodes = useMemo(() => marketLayoutMatches ? marketLayout.nodes : [], [marketLayoutMatches, marketLayout]);
  const marketLayoutError = marketLayoutMatches ? marketLayout.error : "";
  useEffect(() => {
    if (mode !== "players" || viewMode !== "market" || steamLoading ||
      dimensions.width <= 0 || dimensions.height <= 72) return;
    let cancelled = false;
    let request;
    const inputs = { games: filteredPlayers, width: dimensions.width, height: dimensions.height, colorScale, shape: marketShape };
    setMarketLayout(null);
    (async () => {
      try {
        request = requestSteamMarketLayout(filteredPlayers, dimensions.width, dimensions.height, colorScale, marketShape);
        const nodes = await request.promise;
        if (!cancelled) setMarketLayout({ ...inputs, nodes, error: "" });
      } catch (error) {
        if (!cancelled) {
          console.error("Steam Market Mind layout failed:", error);
          setMarketLayout({ ...inputs, nodes: [], error: error?.message || "Unable to prepare Market Mind." });
        }
      }
    })();
    return () => { cancelled = true; request?.cancel(); };
  }, [mode, viewMode, steamLoading, filteredPlayers, marketShape, dimensions.width, dimensions.height, colorScale, marketLayoutRetry]);
  useEffect(() => {
    setBubbleHovered(null);
    setMarketSelected(null);
  }, [bubbleNodes]);

/** Index app_id -> node.data for fast preview lookups */
const nodeIndexRef = useRef(new Map());
useEffect(() => {
  const m = new Map();
  (bubbleNodes || []).forEach((leaf) => {
    const d = leaf?.data;
    if (d?.app_id != null) m.set(String(d.app_id), d);
  });
  nodeIndexRef.current = m;
  // New/changed bubble DOM needs pinned/hover highlight classes re-applied.
  requestAnimationFrame(() => recomputeHighlightDim());
}, [bubbleNodes]);

  useEffect(() => {
  if (mode !== "players" || viewMode !== "market") return;
  if (!bubbleSvgRef.current) return;

  const svg = d3.select(bubbleSvgRef.current);

  const zoom = d3
    .zoom()
    .scaleExtent([0.5, 12])
    .on("zoom", ({ transform }) => {
      // keep state + <g id="steam-mm-zoom"> in sync
      requestAnimationFrame(() => setZoomT(transform));
      d3.select("#steam-mm-zoom").attr("transform", transform);
    });

  svg.call(zoom);

  return () => {
    svg.on(".zoom", null);
  };
}, [mode, viewMode, bubbleNodes, marketShape]);

// Warm-up a few images so popovers feel instant
useEffect(() => {
  if (mode !== "players" || viewMode !== "cards") return;
  const list = visiblePlayers.slice(0, 40).map((g) => g.box_art).filter(Boolean);
  list.forEach((src) => { const img = new Image(); img.decoding = "async"; img.src = src; });
}, [mode, viewMode, visiblePlayers]);


  // Should we show the Market Mind skeleton instead of the real bubbles?
  const showMarketSkeleton =
    mode === "players" &&
    viewMode === "market" &&
    (
      dimensions.width <= 0 ||
      dimensions.height <= 72 ||
      !marketLayoutMatches
    );

  /** Windowed rendering (Followers cards) */
  const FOLLOWERS_INITIAL = 120;
  const FOLLOWERS_BATCH = 80;
  const [followersShown, setFollowersShown] = useState(FOLLOWERS_INITIAL);
  const followersSentinelRef = useRef(null);

  useEffect(() => {
    setFollowersShown(FOLLOWERS_INITIAL);
  }, [
    searchTerm,
    selectedGame,
    selectedTag,
    selectedPublisher,
    followersSort,
    followersDisplayed,
    hiddenFollowers,
    followersLoading,
    mode,
    gamesFilter,
  ]);

  useEffect(() => {
    if (mode !== "followers") return;
    if (!followersSentinelRef.current) return;
    const el = followersSentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setFollowersShown((prev) =>
            prev >= sortedFollowers.length
              ? prev
              : Math.min(sortedFollowers.length, prev + FOLLOWERS_BATCH)
          );
        }
      },
      { root: null, rootMargin: "800px 0px 800px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sortedFollowers.length, mode]);

  const visibleFollowers = useMemo(
    () => sortedFollowers.slice(0, followersShown),
    [sortedFollowers, followersShown]
  );
  const hasMoreFollowers =
    mode === "followers" && followersShown < sortedFollowers.length;

  /** Disable page scroll in Market/Effervescent (Players mode only) */
  useEffect(() => {
    const lock = mode === "players" && viewMode !== "cards";
    const { body, documentElement } = document;
    if (lock) {
      body.style.overflow = "hidden";
      documentElement.style.overflow = "hidden";
      body.style.height = "calc(var(--mg-vh) * 100)";
      documentElement.style.height = "calc(var(--mg-vh) * 100)";
    } else {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    }
    return () => {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    };
  }, [viewMode, mode]);

// Disable "Released This Week" when in Followers + Unreleased
const releasedThisWeekDisabled = mode === "followers" && gamesFilter === "unreleased";

// Show "Online" only when the Players dataset is 100% unfiltered/default
const showOnlineStat = useMemo(() => {
  if (mode !== "players") return false; // Followers doesn't show this stat anyway
  const noTextSearch = (searchTerm || "").trim() === "";
  const noGame = !selectedGame || (Array.isArray(selectedGame) && selectedGame.length === 0);
  const noAllTags = !selectedTag || (Array.isArray(selectedTag) && selectedTag.length === 0);
  const noPrimaryTags = !selectedPrimaryTag || (Array.isArray(selectedPrimaryTag) && selectedPrimaryTag.length === 0);
  const noPublisher = !selectedPublisher || (Array.isArray(selectedPublisher) && selectedPublisher.length === 0);
  const noDevelopers = !selectedDevelopers || (Array.isArray(selectedDevelopers) && selectedDevelopers.length === 0);
  const noDateRange = !selectedDateRange?.[0] && !selectedDateRange?.[1];

  // PRICE: treat "all selected" OR "none selected" as *no price filter*
  const noPriceRange =
    !Array.isArray(selectedPriceRange) ||
    selectedPriceRange.length === PRICE_TIERS.length ||
    selectedPriceRange.length === 0;

  const noHidden = hiddenGames.length === 0;
  const notWeekOnly = !isReleasedThisWeek;

  // AND: the working set is the original "games" array (not a filtered subset)
  const usingFullSet = displayedGames === games;

  return (
    noTextSearch &&
    noGame &&
    noAllTags &&
    noPrimaryTags &&
    noPublisher &&
    noDevelopers &&
    noDateRange &&
    noPriceRange &&
    noHidden &&
    notWeekOnly &&
    usingFullSet
  );
}, [
  mode,
  searchTerm,
  selectedGame,
  selectedTag,
  selectedPrimaryTag,
  selectedPublisher,
  selectedDevelopers,
  selectedDateRange,
  selectedPriceRange,
  hiddenGames.length,
  isReleasedThisWeek,
  displayedGames,
  games,
]);

const [refreshReady, setRefreshReady] = useState(false);

const REFRESH_COOLDOWN_MS = 10 * 60 * 1000;
const refreshTimerRef = useRef(null);

const armRefreshCooldown = () => {
  setRefreshReady(false);

  if (refreshTimerRef.current) {
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = null;
  }

  refreshTimerRef.current = setTimeout(() => {
    setRefreshReady(true);
    refreshTimerRef.current = null;
  }, REFRESH_COOLDOWN_MS);
};

useEffect(() => {
  armRefreshCooldown(); // start hidden, show after 10m
  return () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Auto-refresh: run the same logic as the button every 5 minutes
useEffect(() => {
  const FIVE_MIN = 5 * 60 * 1000;

  const id = setInterval(() => {
    // Don't spam refreshes if the tab isn't visible
    if (document.hidden) return;

    // Only auto-refresh the live Players view; Followers is based on CSV snapshots
    if (mode === "players") {
      refreshSteamData();
    }
  }, FIVE_MIN);

  return () => clearInterval(id);
  // deps: keep closure "fresh" but not crazy noisy
}, [mode, refreshSteamData]);


  const statNumberStyle = {
    color: "#fff",
    fontWeight: 900,
    fontSize: "1.35rem",
    lineHeight: 1,
  };

// Only show backdrop when exactly one game is selected
const chartBgArt =
  chartGames.length === 1 ? chartGames[0]?.metadata?.box_art || "" : "";

  const showChartBgArt =
    !!chartBgArt &&
    !(dataMode === "live" && showRegionBands && chartGames.length === 1);

  // Build per-game chart rows, with optional Followers Daily diff mode
 const buildChartRowsForGame = (g) => {
  const id = g?.metadata?.app_id;
  if (!id) return [];

  // -------- Followers mode (dataMode is "daily" for followers) --------
  if (mode === "followers") {
    const raw = Array.isArray(g.daily) ? g.daily : [];
    if (!raw.length) return [];

    // 1) Total = last data point of each UTC day (one point per day)
if (followersValueMode === "total") {
  const { byDay, days } = bucketFirstLastPerUtcDay(raw);

  return days.map((day) => {
    const d = byDay.get(day);
    return { x: startOfDayUTCISO(day), [id]: d.lastV };
  });
}


  // 2) Daily = day-over-day delta, BUT:
// - if the first-ever dt is on/after 2025-10-01, treat Day 1 as baseline=0 → diff = lastV
// - otherwise (legacy tracking), Day 1 uses intra-day delta → diff = lastV - firstV
const { byDay, days } = bucketFirstLastPerUtcDay(raw);

const firstSeenTs = raw?.[0]?.dt ? new Date(raw[0].dt).getTime() : NaN;
const cutoffTs = Date.parse("2025-10-01T00:00:00.000Z");
const isNewish = Number.isFinite(firstSeenTs) && firstSeenTs >= cutoffTs;

let prevLast = null;

return days.map((day, idx) => {
  const d = byDay.get(day);
  const lastV = Number(d?.lastV);
  const firstV = Number(d?.firstV);

  let diff = 0;

  if (idx === 0) {
    diff = isNewish ? lastV : (lastV - firstV);
  } else {
    diff = prevLast == null ? 0 : (lastV - prevLast);
  }

  prevLast = lastV;

  return { x: startOfDayUTCISO(day), [id]: diff };
});


  }

  // -------- Players mode (unchanged) --------
  return toChartRows(g, dataMode, viewByDaysSinceLaunch);
};



  // merged CCU chart data for Player History compare
  const mergedData = useMemo(() => {
    const bucket = {};
    const src = chartGames.filter(Boolean);
    if (!src.length) return [];

    const _xToNumber = (x) => {
      if (dataMode === "live") return new Date(x).getTime();
      if (viewByDaysSinceLaunch) return Number(x);
      return new Date(x).getTime();
    };

    const lastXPerId = new Map();

    src.forEach((g) => {
      const rows = buildChartRowsForGame(g);
      let maxX = -Infinity;

      rows.forEach((pt) => {
        const id = g.metadata.app_id;
        const xKey = pt.x;
        const v = pt[id];

        if (!bucket[xKey]) bucket[xKey] = { x: xKey };

        const prev = bucket[xKey][id];
        const nv =
          prev == null ? v : Math.max(Number(prev) || 0, Number(v) || 0);
        bucket[xKey][id] = nv;

        const xn = _xToNumber(xKey);
        if (Number.isFinite(xn) && xn > maxX) maxX = xn;
      });

      lastXPerId.set(String(g.metadata.app_id), maxX);
    });

    const arr = Object.values(bucket);
    if (dataMode === "live") {
      arr.sort((a, b) => new Date(a.x) - new Date(b.x));
    } else {
      arr.sort((a, b) =>
        viewByDaysSinceLaunch
          ? parseInt(a.x, 10) - parseInt(b.x, 10)
          : new Date(a.x) - new Date(b.x)
      );
    }

    const ids = src.map((g) => String(g.metadata.app_id));
    const lastVal = Object.create(null);

    for (const row of arr) {
      const rowXn =
        dataMode === "live" || !viewByDaysSinceLaunch
          ? new Date(row.x).getTime()
          : Number(row.x);
      for (const id of ids) {
        const v = row[id];
        if (v == null || !Number.isFinite(Number(v))) {
          const cutoff = lastXPerId.get(id);
          if (lastVal[id] != null && cutoff != null && rowXn <= cutoff) {
            row[id] = lastVal[id];
          }
        } else {
          lastVal[id] = Number(v);
        }
      }
    }

    return arr;
  },  [chartGames,  dataMode,  viewByDaysSinceLaunch,  followersValueMode,  mode]);

  const chartPeakGames = useMemo(() => {
    return chartGames
      .map((game) => {
        const id = String(game.metadata.app_id);
        const peak = Math.max(0, ...(mergedData || []).map((row) => Number(row[id]) || 0));
        return { game, peak };
      })
      .sort((a, b) => b.peak - a.peak);
  }, [chartGames, mergedData]);

  // windowed renderData
  const renderData = useMemo(() => {
    if (!mergedData || mergedData.length === 0) return [];
    const s = Math.max(0, Math.min(winStartIdx, mergedData.length - 1));
    const e = Math.max(s, Math.min(winEndIdx, mergedData.length - 1));
    return mergedData.slice(s, e + 1);
  }, [mergedData, winStartIdx, winEndIdx]);

  const chartTicks = useMemo(() => chartAxisTicks(renderData), [renderData]);

  // Daily Users (Estimated) for single-game Live (7d) view
  const dailyUsersEstimate = useMemo(() => {
  if (dataMode !== "live") return null;
  if (chartGames.length !== 1) return null;

  const g = chartGames[0];
  if (!Array.isArray(g.live) || g.live.length < 2) return null;

  // 🔹 Dynamic hours per user based on CCU shape + days since launch
  const { lowHours, highHours } = getDynamicSessionHoursForGame(g);

  return estimateDailyUsersFromLive(g.live, lowHours, highHours);
}, [dataMode, chartGames]);


  useEffect(() => {
    if (!mergedData || mergedData.length === 0) return;
    if (!hasInitWindow.current) {
      setWinStartIdx(0);
      setWinEndIdx(mergedData.length - 1);
      hasInitWindow.current = true;
    } else {
      setWinStartIdx((prev) =>
        Math.max(0, Math.min(prev, Math.max(0, mergedData.length - 2)))
      );
      setWinEndIdx((prev) =>
        Math.max(1, Math.min(prev, Math.max(1, mergedData.length - 1)))
      );
    }
  }, [mergedData.length]);

  useEffect(() => {
    if (mergedData && mergedData.length > 0) {
      setWinStartIdx(0);
      setWinEndIdx(mergedData.length - 1);
    }
  }, [dataMode, mergedData.length]);

  const labelForIdx = (idx) => {
  if (!mergedData || mergedData.length === 0) return "";
  const x = mergedData[Math.min(Math.max(idx, 0), mergedData.length - 1)].x;

  if (dataMode === "live") return fmtDateTimeUTC(x);
if (viewByDaysSinceLaunch) return String(x);
return fmtDateShortUTC(x);

};


// Match mindSHARE chart palette (blue, beige, orange, magenta, soft blue, green)
const STEAM_CHART_COLORS = [
  "#3fa9f5", // mindGAME blue
 "#F5E6C8", // beige
  "#ff9b70", // orange
 "#33c3eb", // turquoise
  "#f1726a",//red orange
 "#66CCFF", // soft blue
];

const seriesColor = (i) =>
  STEAM_CHART_COLORS[i % STEAM_CHART_COLORS.length];

const seriesIntro = useSeriesIntroAnimation(chartGames.map(game => String(game.metadata.app_id)));

// Track whether multi-select modifier (Ctrl/Cmd) is held down
const [multiKeyDown, setMultiKeyDown] = useState(false);

useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === "Control" || e.key === "Meta") {
      setMultiKeyDown(true);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === "Control" || e.key === "Meta") {
      setMultiKeyDown(false);
    }
  };

  const handleBlur = () => {
    // If the window loses focus, reset
    setMultiKeyDown(false);
  };

  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);
  window.addEventListener("blur", handleBlur);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("keyup", handleKeyUp);
    window.removeEventListener("blur", handleBlur);
  };
}, []);


// Add a game from Steam cards into the compare chart
const handleAddToChart = async (game, retry = false) => {
  if (isPublicPreview) return;
  if (!game?.app_id) return;
  const appId = String(game.app_id);

  // Already in chart? no-op
  if (!retry && chartGames.some((g) => String(g.metadata.app_id) === appId)) return;

  // Shared metadata for both Players and Followers charts
  const metadataForChart = {
    app_id: appId,
    name: game.name,
    box_art: game.box_art,
    release_date: game.release_date,
    full_price: game.full_price,
  };


  // 1) Optimistic placeholder so user sees the chip instantly
  setChartGames(prev => retry
    ? prev.map(g => String(g.metadata.app_id) === appId ? { ...g, loading: true, error: "" } : g)
    : [...prev, { metadata: metadataForChart, live: [], daily: [], loading: true }]);

  try {
    let live = [];
    let daily = [];

    if (mode === "players") {
      // Render the live window without waiting for the all-time daily archive.
      [live, daily] = await Promise.all([
        loadLiveSeries(appId).then(liveRows => {
          setChartGames(prev => prev.map(g => String(g.metadata.app_id) === appId
            ? { ...g, live: liveRows } : g));
          return liveRows;
        }),
        loadDailySeries(appId),
      ]);
    } else {
      // 👥 Followers mode: last 90 days Followers, daily series only
      daily = await loadFollowersSeries(appId, 90);
      live = []; // no live followers series
    }

    // 2) Hydrate that entry with real data
    setChartGames((prev) =>
      prev.map((g) =>
        String(g.metadata.app_id) === appId
          ? { ...g, live, daily, loading: false, error: "" }
          : g
      )
    );

  } catch (e) {
    console.error("❌ Failed to load history for chart:", e);
    setChartGames((prev) =>
      prev.map((g) =>
        String(g.metadata.app_id) === appId
          ? { ...g, loading: false, error: "History could not be loaded. Please retry." }
          : g
      )
    );
  }
};

  const handleRemoveFromChart = (appId) => {
    if (isPublicPreview) return;
    const idStr = String(appId);
    setChartGames((prev) =>
      prev.filter((g) => String(g.metadata.app_id) !== idStr)
    );
  };

  /** Video embed helper (shared) */
  const getVideoEmbed = (raw) => {
    if (!raw) return null;
    const url = raw.trim();
    const lower = url.toLowerCase();

    if (/\.(mp4|webm|ogg)(\?|#|$)/.test(lower)) {
      return { type: "file", src: url };
    }
    const yt = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/
    );
    if (yt) {
      const id = yt[1];
      return {
        type: "youtube",
        src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&showinfo=0&loop=1&playlist=${id}`,
      };
    }
    return null;
  };

  const [hoveredCards, setHoveredCards] = useState({});
  const cardRefs = useRef({});

  const handleMouseLeave = (appId) => {
    setHoveredCards(prev => ({ ...prev, [appId]: false }));
  };

  const getShineStyle = (hovered, card) => {
    if (!card) return {};
    const style = {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      borderRadius: "14px",
      opacity: hovered ? 1 : 0,
      transition: "opacity 150ms ease",
      background: `radial-gradient(600px at var(--sx,50%) var(--sy,50%), rgba(255,255,255,0.18), transparent 40%), 
                 linear-gradient(120deg, rgba(255,255,255,0.12), rgba(63,169,245,0.12) 20%, transparent 60%)`,
      mixBlendMode: "screen",
      zIndex: 3,
    };
    return style;
  };



  // Effervescent hover + reset on filter changes
  const [effHover, setEffHover] = useState(null);
  useEffect(() => {
    if (mode === "players" && viewMode === "effervescent") {
      setEffHover(null);
      setStreamNonce((n) => n + 1);
    }
  }, [viewMode, mode]);

  const effKey = useMemo(() => {
    const ids = visiblePlayers.map((g) => g.app_id).join(",");
    return `eff:${ids}:${streamNonce}`;
  }, [visiblePlayers, streamNonce]);

  const effContainerRef = useRef(null);

  /** If user switches to Followers, force viewMode to cards (Followers are card-only) */
  useEffect(() => {
    if (mode === "followers" && viewMode !== "cards") setViewMode("cards");
  }, [mode, viewMode]);

// When switching between Players and Followers, reset the compare chart
useEffect(() => {
  // 🔒 hard lock: don't wipe while a restore/apply is in progress
  if (restoringRef.current) return;

  setChartGames([]);
  setHighlightedSeries(null);
  hasInitWindow.current = false;
  setWinStartIdx(0);
  setWinEndIdx(0);

  if (mode === "followers") {
    setDataMode("daily");
    setViewByDaysSinceLaunch(false);
    setShowRegionBands(false);
  }
}, [mode]);


// Double-safeguard: if Followers pill is Unreleased, ensure the week filter is OFF (and data reset)
useEffect(() => {
  if (mode === "followers" && gamesFilter === "unreleased" && isReleasedThisWeek) {
    setIsReleasedThisWeek(false);
    setFollowersDisplayed(followersGames);
  }
}, [mode, gamesFilter, isReleasedThisWeek, followersGames]);

  /* ============================
     UI
     ============================ */
  const activeMarketNode = marketSelected || bubbleHovered;
    return (
    <div
      className="steam-dashboard"
      style={{
        display: "flex",
        background: "#000",
        color: "#fff",
        minHeight: mode === "players" && viewMode === "market" ? 0 : "calc(var(--mg-vh) * 100)",
      }}
    >
      {filtersOpen && !isPublicPreview && (
        <div
          className="mg-steam-filter-backdrop"
          onMouseEnter={() => {
            if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
          }}
          onClick={() => setFiltersOpen(false)}
          aria-hidden="true"
        />
      )}

      <MindshareHeaderControls visible neutralViews views={STEAM_VIEWS}
        viewMode={viewMode} onViewChange={isPublicPreview ? () => {} : setViewMode}
        onOpenFilters={isPublicPreview ? () => {} : () => setFiltersOpen(open => !open)} filtersOpen={!isPublicPreview && filtersOpen}
        searchTerm={isPublicPreview ? "" : searchTerm} onSearchChange={isPublicPreview ? () => {} : setSearchTerm}
        readOnly={isPublicPreview}
        targetId="steam-header-controls" savedControlsId="steam-header-saved-controls"
        centerSearchTargetId="steam-header-search"
        ariaLabel="Steam views and filters" searchAriaLabel="Search Steam games" />
      {headerStatsTarget && !initialDashboardLoading && createPortal(
        <div className="steam-header-stats mg-dashboard-content" role="group" aria-label="Steam statistics">
          {[
            ...(showOnlineStat && !inChartMode ? [[steamTotalOnline.toLocaleString(), "Online"]] : []),
            [headerTotalCcu.toLocaleString(), "Playing Now"],
            [`${headerPlayersShareDisplay}%`, "of Players"],
            ...(!inChartMode ? [[filteredPlayers.length.toLocaleString(), "Active Games"]] : []),
          ].map(([value, label]) => <span className="steam-header-stat" key={label}>
            <strong>{value}</strong><span>{label}</span>
          </span>)}
          <button type="button" className="ms-header-icon" aria-label="Refresh Steam players"
            title="Refresh Steam players" onClick={handleQuickRefresh} disabled={!refreshReady}>
            <RotateCcw size={14} />
          </button>
        </div>, headerStatsTarget)}

      {/* Steam filter workspace */}
      <MGFilterControl
        className="mg-steam-filter-popover steam-filter-workspace"
        role="dialog"
        aria-label="Steam filters"
        aria-hidden={!filtersOpen}
        onMouseEnter={() => {
          if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
        }}
        style={{
          position: "fixed",
          top: 82,
          left: 20,
          height: "auto",
          maxHeight: "calc(calc(var(--mg-vh) * 100) - 108px)",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "20px",
          background: "#000",
          borderRight: "1px solid #181818",
          width: "min(440px, calc(calc(var(--mg-vw) * 100) - 40px))",
          minWidth: 0,
          flexShrink: 0,
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: "12px 16px",
          zIndex: 10000,
          opacity: filtersOpen ? 1 : 0,
          visibility: filtersOpen ? "visible" : "hidden",
          pointerEvents: filtersOpen ? "auto" : "none",
          transform: filtersOpen ? "translateY(0)" : "translateY(-12px)",
          boxShadow: "0 18px 46px rgba(0,0,0,0.72)",
          transition: "opacity 150ms ease, transform 150ms ease, visibility 150ms ease",
        }}
      >

        {!isPublicPreview && <SavedViewsManager
  key={savedViewsKey}
  type="steam"
  currentFilters={{
    searchTerm,
    tag: selectedTag,
    primaryTag: selectedPrimaryTag,
    game: selectedGame,
    publisher: selectedPublisher,
    developer: selectedDevelopers,
    priceRange: selectedPriceRange,
    dateRange: selectedDateRange,
    viewMode,
    marketShape,
    pageMode: mode,
    gamesFilter,
    releasedThisWeek: isReleasedThisWeek,
    chart: {
      ids: (chartGames || []).map((g) => String(g?.metadata?.app_id)).filter(Boolean),
      dataMode,
      viewByDaysSinceLaunch,
      showRegionBands,
      followersValueMode,
      highlightedSeries,
      winStartIdx,
      winEndIdx,
    },
  }}

         onApplyFilters={(filters) => {
  // ⛔ Ignore the default-view auto-apply that fires right after Reset All
  // (SavedViewsManager remounts via savedViewsKey and would otherwise
  // re-apply the default view, undoing the reset and forcing Cards view).
  if (lastResetAtRef.current && Date.now() - lastResetAtRef.current < 5000) return;

  // 🔒 lock immediately (synchronous)
  restoringRef.current = true;
  restoreTokenRef.current += 1;

  setSearchTerm(filters.searchTerm ?? "");
  setSelectedGame(filters.game ?? null);
  setSelectedTag(filters.tag ?? null);
  setSelectedPrimaryTag(filters.primaryTag ?? null);
  setSelectedPublisher(filters.publisher ?? null);
  setSelectedDevelopers(filters.developer ?? null);

  const incomingPrice = filters.priceRange;
  if (Array.isArray(incomingPrice) && incomingPrice.every((id) => typeof id === "string")) {
    setSelectedPriceRange(incomingPrice);
  } else {
    setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
  }

  const dateRange = filters.dateRange ?? [null, null];
  if (dateRange[0] && typeof dateRange[0] === "string") dateRange[0] = new Date(dateRange[0]);
  if (dateRange[1] && typeof dateRange[1] === "string") dateRange[1] = new Date(dateRange[1]);
  setSelectedDateRange(dateRange);

const hasChartIds =
  Array.isArray(filters?.chart?.ids) && filters.chart.ids.length > 0;

// ✅ If the saved view includes a chart, force Cards so the chart UI exists.
setViewMode(hasChartIds ? "cards" : (filters.viewMode ?? "cards"));
setMarketShape(filters.marketShape === "bubbles" ? "bubbles" : "squares");
setMode(filters.pageMode ?? "players");

setGamesFilter(filters.gamesFilter ?? "all");

// ✅ Apply Released This Week from saved view
const nextPageMode = filters.pageMode ?? "players";
const nextGamesFilter = filters.gamesFilter ?? "all";

// Disallow week filter when Followers + Unreleased (since button is disabled there)
const wantWeek = !!filters.releasedThisWeek;
const weekAllowed = !(nextPageMode === "followers" && nextGamesFilter === "unreleased");
const appliedWeek = weekAllowed ? wantWeek : false;

setIsReleasedThisWeek(appliedWeek);

if (appliedWeek) {
  if (nextPageMode === "players") {
    setDisplayedGames(games.filter((g) => withinLastNDays(g.release_date, 7)));
    setFollowersDisplayed(followersGames); // keep followers base set
  } else {
    setFollowersDisplayed(followersGames.filter((g) => withinLastNDays(g.release_date, 7)));
    setDisplayedGames(games); // keep players base set
  }
} else {
  setDisplayedGames(games);
  setFollowersDisplayed(followersGames);
}


if ((filters.pageMode ?? "players") === "followers") {
  setFollowersValueMode(filters?.chart?.followersValueMode ?? "total");
}

  // queue chart restore
  setPendingChartRestore(filters.chart ?? null);
}}


onClearFilters={() => {
  // 1) Clear ALL selections (no active filters)
  setSelectedGame(null);
  setSelectedTag(null);
  setSelectedPublisher(null);
  setSelectedDevelopers(null);
  restoreTokenRef.current += 1;
  setChartGames([]);
     setPendingChartRestore(null);
      restoringRef.current = false;
  setHighlightedSeries(null);
  hasInitWindow.current = false;
  setWinStartIdx(0);
  setWinEndIdx(0);
     setFollowersValueMode("total");
  setDataMode("live");
  setViewByDaysSinceLaunch(false);
  setShowRegionBands(false);



  // Price/date: set to "no filter" states (so the blocks don't run)
      setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
  setSelectedDateRange([null, null]);     // <— IMPORTANT (don’t pass releaseDateRange)


  // 2) Restore full datasets & hidden states
  setHiddenGames([]);
  setHiddenFollowers([]);
  setDisplayedGames(games);               // back to full
  setFollowersDisplayed(followersGames);  // back to full

  // 3) Reset misc UI state that can affect counts
  setIsReleasedThisWeek(false);
  setSearchTerm("");

  // ⛔ Do NOT touch viewMode/mode here — clearing filters must keep the user
  // on the view they clicked Reset/Clear from (e.g. stay on Market Mind).
  setPinnedHighlights([]);
  setPreview(null);
  previewRef.current = null;
}}
        />}

        <VirtualizedDropdown
  placeholder="All Games"
previewType="game"
onPreview={(type, value) => applyPreview({ type, value })}
onMenuClose={() => applyPreview(null)}
showHighlightToggle={viewMode === "market"}
isHighlighted={(val) => isHighlightPinned("game", val)}
onToggleHighlight={(val) => toggleHighlightPin("game", val)}

  /* MULTI value shape */
  value={
    Array.isArray(selectedGame)
      ? selectedGame
      : selectedGame
      ? [selectedGame]
      : []
  }
  onChange={(opts, meta) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedGame(null);
    } else {
      setSelectedGame(Array.isArray(opts) ? opts : [opts]);
    }
    // do NOT modify gameQuery here — preserves typed text
  }}
  options={gamesOptionsByCCU}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

  /* keep the input text stable across selections */
  inputValue={gameQuery}
  onInputChange={(val, meta) => {
    // Only update for actual typing
    if (meta?.action === "input-change") {
      setGameQuery(val ?? "");
    }
    // ignore 'set-value', 'menu-close', 'input-blur', etc.
  }}

  /* optional: clear the query when the menu closes */
  // onMenuClose={() => setGameQuery("")}

  maxMenuWidth={340}
/>

<VirtualizedDropdown
  placeholder="Primary Tags"
  /* highlighter restricted to first-5 tags */
  previewType="primaryTag"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}
  showHighlightToggle={viewMode === "market"}
  isHighlighted={(val) => isHighlightPinned("primaryTag", val)}
  onToggleHighlight={(val) => toggleHighlightPin("primaryTag", val)}

  /* MULTI selection (same UX as All Tags) */
  value={
    Array.isArray(selectedPrimaryTag)
      ? selectedPrimaryTag
      : selectedPrimaryTag
      ? [selectedPrimaryTag]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedPrimaryTag(null);
    } else {
      setSelectedPrimaryTag(Array.isArray(opts) ? opts : [opts]);
    }
  }}

  /* Simple alphabetized list from first-5 tags only */
  options={primaryTagsAlphaOptions}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

  /* keep the input text stable while selecting */
  inputValue={primaryTagQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") {
      setPrimaryTagQuery(val ?? "");
    }
  }}

  maxMenuWidth={340}
/>

<VirtualizedDropdown
  placeholder="All Tags"
  /* NEW: live highlight while hovering tag options */
  previewType="tag"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}
  showHighlightToggle={viewMode === "market"}
  isHighlighted={(val) => isHighlightPinned("tag", val)}
  onToggleHighlight={(val) => toggleHighlightPin("tag", val)}

  /* MULTI value shape */
  value={
    Array.isArray(selectedTag)
      ? selectedTag
      : selectedTag
      ? [selectedTag]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedTag(null);
    } else {
      setSelectedTag(Array.isArray(opts) ? opts : [opts]);
    }
    // keep typed text intact
  }}
  options={tagsOptionsByCCU}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

  /* keep the input text stable across selections */
  inputValue={tagQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") {
      setTagQuery(val ?? "");
    }
  }}

  maxMenuWidth={340}
/>


<VirtualizedDropdown
  placeholder="All Publishers"

/* NEW: live highlight while hovering publisher options */
  previewType="publisher"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}
  showHighlightToggle={viewMode === "market"}
  isHighlighted={(val) => isHighlightPinned("publisher", val)}
  onToggleHighlight={(val) => toggleHighlightPin("publisher", val)}

  /* MULTI value shape */
  value={
    Array.isArray(selectedPublisher)
      ? selectedPublisher
      : selectedPublisher
      ? [selectedPublisher]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedPublisher(null);
    } else {
      setSelectedPublisher(Array.isArray(opts) ? opts : [opts]);
    }
  }}

  options={(pubQuery || "").length >= 2 ? publisherOptions : publisherOptionsByCCU}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

/* keep the fast-index build and typed text behavior */
  onMenuOpen={onPublishersMenuOpen}
  inputValue={pubQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") setPubQuery(val ?? "");
  }}

  maxMenuWidth={340}
/>


<VirtualizedDropdown
  placeholder="All Developers"

/* NEW: live highlight while hovering developer options */
  previewType="developer"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}
  showHighlightToggle={viewMode === "market"}
  isHighlighted={(val) => isHighlightPinned("developer", val)}
  onToggleHighlight={(val) => toggleHighlightPin("developer", val)}

  /* MULTI value shape */
  value={
    Array.isArray(selectedDevelopers)
      ? selectedDevelopers
      : selectedDevelopers
      ? [selectedDevelopers]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedDevelopers(null);
    } else {
      setSelectedDevelopers(Array.isArray(opts) ? opts : [opts]);
    }
  }}

  options={(devQuery || "").length >= 2 ? developerOptions : developerOptionsByCCU}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

/* keep typed text intact */
  inputValue={devQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") setDevQuery(val ?? "");
  }}

  maxMenuWidth={340}
/>


                {/* Price goes directly under Developers, same spacing as dropdowns */}
<PriceSlider
  tiers={PRICE_TIERS}
  selected={selectedPriceRange}
  onChange={setSelectedPriceRange}
/>

{/* Release Date: always expanded, same row spacing as the other filters */}
<DateSlider
  range={releaseDateRange}
  selected={selectedDateRange}
  onChange={setSelectedDateRange}
  onClear={() => setSelectedDateRange([null, null])}
  forceClose={!filtersOpen}
/>


        {/* Released This Week */}
        <button
          className="steam-popular-week"
          disabled={releasedThisWeekDisabled}
          onClick={() => {
            if (releasedThisWeekDisabled) return;

            if (isReleasedThisWeek) {
              setDisplayedGames(games);
              setFollowersDisplayed(followersGames);
              setIsReleasedThisWeek(false);
            } else {
              if (mode === "players") {
                handleReleasedThisWeekPlayers();
              } else {
                handleReleasedThisWeekFollowers();
              }
              setIsReleasedThisWeek(true);
            }
          }}
          title={
            releasedThisWeekDisabled
              ? "Disabled on Followers → Unreleased (released games are never unreleased)"
              : undefined
          }
          style={{
            width: "100%",
            minHeight: 36,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid",
            boxSizing: "border-box",
            fontWeight: "normal",

            background: releasedThisWeekDisabled
              ? "#050505"
              : isReleasedThisWeek
              ? "#3fa9f5"
              : "#050505",
            borderColor: releasedThisWeekDisabled
              ? "#333"
              : isReleasedThisWeek
              ? "#3fa9f5"
              : "#444",
            color: releasedThisWeekDisabled
              ? "#777"
              : isReleasedThisWeek
              ? "#000"
              : "rgba(187,187,187,0.75)",
            cursor: releasedThisWeekDisabled ? "not-allowed" : "pointer",
            opacity: releasedThisWeekDisabled ? 0.6 : 1,
          }}
        >
          Released This Week
        </button>

        <button className="steam-popular-year" onClick={() => applyPopularFilter("year")} style={{ width: "100%", minHeight: 36, padding: "8px 12px", borderRadius: 8, border: "1px solid #444", background: popularFilter === "year" ? "#3fa9f5" : "#050505", color: popularFilter === "year" ? "#000" : "rgba(187,187,187,0.75)", cursor: "pointer" }}>
          Released This Year
        </button>

        <button className="steam-popular-free" onClick={() => applyPopularFilter("free")} style={{ width: "100%", minHeight: 36, padding: "8px 12px", borderRadius: 8, border: "1px solid #444", background: popularFilter === "free" ? "#3fa9f5" : "#050505", color: popularFilter === "free" ? "#000" : "rgba(187,187,187,0.75)", cursor: "pointer" }}>
          Free to Play
        </button>


        {/* Mode-specific sidebar actions */}
        {mode === "players" ? (
          <>
                        <button
              onClick={handleResetPlayers}
              style={{
                width: "100%",
                minHeight: 36,
                background: "#050505",
                border: "1px solid #444",
                borderRadius: 8,
                padding: "8px 12px",
                color: "rgba(187,187,187,0.75)",
                fontWeight: "normal",
                fontFamily: "inherit",
                lineHeight: 1.3,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
             ↻ Reset All
            </button>
            <CsvExportButton
              onClick={handleExportPlayers}
              style={{
                width: "100%",
                minHeight: 36,
                background: "#050505",
                border: "1px solid #444",
                boxSizing: "border-box",
                borderRadius: 8,
                padding: "8px 12px",
                color: "rgba(187,187,187,0.75)",
                fontWeight: "normal",
                fontFamily: "inherit",
                lineHeight: 1.3,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              ⬇ Export CSV
            </CsvExportButton>

          </>
        ) : (
  <>
    <button
      onClick={handleResetFollowers}
      style={{
        width: "100%",
        minHeight: 36,
        background: "#050505",
        border: "1px solid #444",
        borderRadius: 8,
        padding: "8px 12px",
        color: "rgba(187,187,187,0.75)",
        fontWeight: "normal",
        fontFamily: "inherit",
        lineHeight: 1.3,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      ↻ Reset All
    </button>

    <CsvExportButton
      onClick={handleExportFollowers}
      style={{
        width: "100%",
        minHeight: 36,
        background: "#050505",
        border: "1px solid #444",
        boxSizing: "border-box",
        borderRadius: 8,
        padding: "8px 12px",
        color: "rgba(187,187,187,0.75)",
        fontWeight: "normal",
        fontFamily: "inherit",
        lineHeight: 1.3,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      ⬇ Export CSV
    </CsvExportButton>
  </>
)}
  <div className="steam-filter-divider steam-filter-divider-popular" aria-hidden="true">Popular</div>
  <div className="steam-filter-divider steam-filter-divider-filters" aria-hidden="true">Filters</div>
  <div className="steam-filter-divider steam-filter-divider-tools" aria-hidden="true">Tools</div>
      </MGFilterControl>

      {/* Main */}
      <div
  ref={mainRef}
  style={{
    flex: 1,
    padding: mode === "players" && viewMode !== "cards" ? "20px 20px 0 20px" : 20, // bottom=0 when Market/Bubbles
    paddingTop: viewMode === "cards" ? 4 : undefined,
    position: "relative",
    height: mode === "players" && viewMode === "market" ? dimensions.height + 72 : mode === "players" && viewMode !== "cards" ? "calc(var(--mg-vh) * 100)" : "auto",
    overflow: mode === "players" && viewMode !== "cards" ? "hidden" : "visible",
  }}
>
<div ref={headerRef}>
  {mode === "players" ? viewMode === "market" && (
    <div className="steam-market-controls">
      <div className="mm-layout-comparison" role="group" aria-label="Market Mind layout">
        {[["bubbles", "Circles"], ["squares", "Squares"]].map(([value, label]) =>
          <button key={value} type="button" aria-pressed={marketShape === value}
            onClick={() => setMarketShape(value)}>{label}</button>)}
      </div>
      <span className="steam-market-note">Size and color = live players · largest games at the center</span>
    </div>
  ) : (
    <FollowersHeader
      filteredFollowers={filteredFollowers}
      followersTotal={headerFollowersTotal}   // 👈 chart-aware total
      gamesFilter={gamesFilter}
      setGamesFilter={setGamesFilter}
      followersSort={followersSort}
      setFollowersSort={setFollowersSort}
      statNumberStyle={statNumberStyle}
      onOpenFilters={() => setFiltersOpen(true)}
      chartMode={inFollowersChartMode}        // 👈 tell header to switch layout
    />
  )}
</div>



        {/* Player History compare chart (only Players / Cards) */}
        {viewMode === "cards" && chartGames.length > 0 && (
          <div className="steam-comparison-chart" style={{ marginBottom: 20, position: "relative", overflow: "hidden", borderRadius: 2, background: "#050505", border: "1px solid #222" }}>
            {showChartBgArt && (
              <>
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${chartBgArt})`,
                    backgroundSize: "cover",
                    backgroundPosition: "top center",
                    filter: "saturate(130%) brightness(1.3) contrast(1.05)",
                    opacity: 0.2,
                  }}
                />
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "radial-gradient(120% 140% at 50% -20%, rgba(63,169,245,0.22), transparent 45%), linear-gradient(180deg, rgba(0,0,0,0.80), rgba(0,0,0,0.96))",
                  }}
                />
              </>
            )}
            <div style={{ position: "relative", zIndex: 1 }}>
              <>
                {/* controls row */}
               <div
  style={{
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    margin: "0 12px 10px",
    paddingTop: 12,
  }}
>
  {/* Only Players see Live / Daily & region/days controls */}
  {mode === "players" ? (
    <>
      {/* Live vs Daily toggle (unchanged) */}
      <div
        style={{
          display: "inline-flex",
          border: "1px solid #2a2a2a",
          borderRadius: 7,
          overflow: "hidden",
        }}
      >
        <button
          onClick={() => setDataMode("live")}
          aria-pressed={dataMode === "live"}
          style={{
            ...steamChartPillStyle(dataMode === "live"),
            borderRadius: 0,
            borderRight: "1px solid #2a2a2a",
          }}
        >
          Live (7D)
        </button>
        <button
          onClick={() => setDataMode("daily")}
          aria-pressed={dataMode === "daily"}
          style={{
            ...steamChartPillStyle(dataMode === "daily"),
            borderRadius: 0,
          }}
        >
          Daily
        </button>
      </div>

      {/* Region bands vs Days Since Launch (Players only) */}
      {dataMode === "live" ? (
  <>
    <label
      style={{
        fontSize: 13,
        color: "#bbb",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
      title="Show/Hide APAC / EU / NA prime-hour bands"
    >
      <input
        type="checkbox"
        checked={showRegionBands}
        onChange={() => setShowRegionBands((v) => !v)}
        style={{
          marginRight: 6,
          verticalAlign: "middle",
        }}
      />
      View Region Prime Hours
    </label>

    {showRegionBands && (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          marginLeft: 10,
        }}
      >
      {/* APAC (yellow) */}
<span
  style={{
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    // darker, translucent chip instead of neon square
    background: "rgba(250, 204, 21, 0.35)",       // was "#facc15"
    border: "1px solid rgba(148, 114, 12, 0.9)",  // dark edge
    boxShadow: "0 0 0 1px rgba(0,0,0,0.7)",       // subtle dark inset
  }}
/>
<span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>
  APAC
</span>

{/* EU (green) */}
<span
  style={{
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    background: "rgba(74, 222, 128, 0.35)",       // was "#4ade80"
    border: "1px solid rgba(22, 120, 72, 0.9)",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.7)",
  }}
/>
<span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>
  EU
</span>

{/* NA (blue) */}
<span
  style={{
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    background: "rgba(56, 189, 248, 0.35)",       // was "#38bdf8"
    border: "1px solid rgba(12, 105, 148, 0.9)",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.7)",
  }}
/>
<span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>
  NA
</span>

      </div>
    )}
  </>
) : (
 // Days-since-launch toggle (unchanged)
        <div
          style={{
            display: "inline-flex",
            border: "1px solid #2a2a2a",
            borderRadius: 7,
            overflow: "hidden",
          }}
        >
          <button
            type="button"
            onClick={() => setViewByDaysSinceLaunch(false)}
            aria-pressed={!viewByDaysSinceLaunch}
            style={{ ...steamChartPillStyle(!viewByDaysSinceLaunch), borderRadius: 0, borderRight: "1px solid #2a2a2a" }}
          >
            Calendar Date
          </button>
          <button
            type="button"
            onClick={() => setViewByDaysSinceLaunch(true)}
            aria-pressed={viewByDaysSinceLaunch}
            style={{ ...steamChartPillStyle(viewByDaysSinceLaunch), borderRadius: 0 }}
          >
            Days Since Launch
          </button>
        </div>
      )}
    </>
  ) : (
    // Followers mode: Total vs Daily toggle
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      {/* Total | Daily pills */}
      <div
        style={{
          display: "inline-flex",
          border: "1px solid #2a2a2a",
          borderRadius: 7,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setFollowersValueMode("total")}
          aria-pressed={followersValueMode === "total"}
          style={{
            ...steamChartPillStyle(followersValueMode === "total"),
            borderRadius: 0,
            borderRight: "1px solid #2a2a2a",
          }}
        >
          Total
        </button>
        <button
          type="button"
          onClick={() => setFollowersValueMode("daily")}
          aria-pressed={followersValueMode === "daily"}
          style={{
            ...steamChartPillStyle(followersValueMode === "daily"),
            borderRadius: 0,
          }}
        >
          Daily
        </button>
      </div>

      {/* Label */}
      <span
        style={{
          fontSize: 13,
          color: "#bbb",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        Followers · Last 90 Days
      </span>
    </div>
  )}

</div>

               {/* Chart itself */}
        <div style={{ paddingRight: 0 }}>
          <div
            style={{
              position: "relative",
              borderRadius: 0,
              overflow: "hidden",
              border: "none",
              background: "transparent",
            }}
          >
            {/* Foreground content: either skeleton or chart */}
            <div
  style={{
    position: "relative",
    zIndex: 1,
    padding: "8px 0 0 0",
    width: "100%",
    height: steamChartHeight,
  }}
>
  {/* Daily Users estimate badge (single-game Live view only) */}
  {dailyUsersEstimate && (
    <div
      style={{
        position: "absolute",
        top: 14,
        right: 18,
        zIndex: 3,
        textAlign: "right",
        fontSize: 11,
        color: "#cfd8e3",
      }}
    >
      <div
        style={{
          letterSpacing: "0.08em",
          fontWeight: 600,
          color: "#9ca3af",
          marginBottom: 2,
        }}
      >
        Daily Users (Est.)
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 800,
          color: "#f9fafb",
          lineHeight: 1.1,
        }}
      >
        {dailyUsersEstimate.low.toLocaleString()} –{" "}
        {dailyUsersEstimate.high.toLocaleString()}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#6b7280",
          marginTop: 2,
        }}
      >
        Last 24H
      </div>
    </div>
  )}

  {chartGames.some(game => game.error) && <div role="alert"
    style={{ position: "absolute", top: 8, left: 12, zIndex: 5, color: "#f4f0e9", fontSize: 12 }}>
    {chartGames.filter(game => game.error).map(game => <div key={game.metadata.app_id}>
      {game.metadata.name}: {game.error}{" "}
      <button type="button" onClick={() => handleAddToChart(game.metadata, true)}>Retry history</button>
    </div>)}
  </div>}
  {renderData.length === 0 ? (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        color: "#999",
      }}
   >
      {chartGames.some(game => game.loading)
        ? (mode === "followers" ? "Loading follower history…" : "Loading player history…")
        : "No history is available for the selected games and chart mode."}
    </div>
  ) : (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={renderData} margin={{ top: 40, right: 40, left: 8, bottom: 8 }}>
        {/* existing chart contents */}

                    {/* existing CartesianGrid / XAxis / YAxis / Tooltip / ReferenceDot / bands / Area series */}
                    <CartesianGrid stroke="#222" />
                    <XAxis
  dataKey="x"
  ticks={chartTicks}
  stroke="#ddd"
  tick={{ fontSize: 11 }}
  tickLine={false}
  minTickGap={22}
  tickFormatter={(tick) =>
    dataMode === "live"
      ? fmtDateTimeUTC(tick)
      : viewByDaysSinceLaunch
      ? String(tick)
      : fmtDateShortUTC(tick)
  }
/>

                    <YAxis
  stroke="#ddd"
  width={90}
  tick={{ fontSize: 11, fill: "#ddd" }}
  tickLine={false}
  domain={[0, (max) => roundYAxisMax(max)]}
  allowDataOverflow
  tickFormatter={(tick) =>
    new Intl.NumberFormat("en-US").format(tick)
  }
/>

                   <Tooltip content={renderTooltip} />


                        {/* Peak dots for each series */}
                 {(() => {
  const peaks = {};
  for (const row of renderData) {
    for (const key of Object.keys(row)) {
      if (key === "x") continue;
      const v = Number(row[key]);
      if (!Number.isFinite(v)) continue;
      if (!peaks[key] || v > peaks[key].y) {
        peaks[key] = { x: row.x, y: v };
      }
    }
  }

  // Normalize highlight into an array
  const selectedIds = Array.isArray(highlightedSeries)
    ? highlightedSeries
    : highlightedSeries
    ? [String(highlightedSeries)]
    : [];

  return Object.entries(peaks).map(([id, { x, y }]) => {
    const seriesIndex = chartGames.findIndex(
      (g) => String(g.metadata.app_id) === String(id)
    );

    const game =
      seriesIndex >= 0 ? chartGames[seriesIndex] : null;
    const gameName = game?.metadata?.name || String(id);

    const color = seriesColor(
      seriesIndex >= 0 ? seriesIndex : 0
    );

    const isDimmed =
      selectedIds.length > 0 && !selectedIds.includes(String(id));

    const labelColor = isDimmed ? "#777" : color;

const labelPosition =
      mode === "followers" ? "left" : "top";

    return (
      <ReferenceDot
        key={`peak-${id}`}
        x={x}
        y={y}
        r={5}
        ifOverflow="extendDomain"
        fill={isDimmed ? "#444" : color}
        stroke="#000"
        label={{
          value: gameName,
          position: labelPosition,
          fill: labelColor,
          fontSize: 12,
          fontWeight: 700,
        }}
      />
    );
  });
})()}





                        {/* Region bands for live mode */}
                        {dataMode === "live" &&
                          showRegionBands &&
                          renderData.length > 0 &&
                          (() => {
                            const first = new Date(
                              renderData[0].x
                            ).getTime();
                            const last = new Date(
                              renderData[renderData.length - 1].x
                            ).getTime();
                            const seenDays = new Set();
                            const out = [];
                          const pushBand = (x1ms, x2ms, fill, key /*, tag */) => {
  const x1 = Math.max(first, x1ms);
  const x2 = Math.min(last, x2ms);
  if (x2 <= x1) return;
  out.push(
    <ReferenceArea
      key={key}
      x1={new Date(x1).toISOString()}
      x2={new Date(x2).toISOString()}
      ifOverflow="hidden"
      fill={fill}
      stroke="none"
    />
  );
};

                            for (const row of renderData) {
                              const d = new Date(row.x);
                              if (Number.isNaN(d)) continue;
                              const y = d.getUTCFullYear();
                              const m = d.getUTCMonth();
                              const day = d.getUTCDate();
                              const dayKey = `${y}-${m}-${day}`;
                              if (seenDays.has(dayKey)) continue;
                              seenDays.add(dayKey);

                              // APAC
                              {
                                const s = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  ASIA_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  ASIA_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s,
                                  e,
                                  ASIA_BAND_FILL,
                                  `apac-${dayKey}`,
                                  "APAC"
                                );
                              }
                              // EU
                              {
                                const s = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  EU_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  EU_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s,
                                  e,
                                  EU_BAND_FILL,
                                  `eu-${dayKey}`,
                                  "EU"
                                );
                              }
                              // NA (may wrap)
                              if (
                                NA_UTC_START_HOUR <=
                                NA_UTC_END_HOUR
                              ) {
                                const s = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  NA_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  NA_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s,
                                  e,
                                  NA_BAND_FILL,
                                  `na-${dayKey}`,
                                  "NA"
                                );
                              } else {
                                const s1 = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  NA_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e1 = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  24,
                                  0,
                                  0
                                );
                                pushBand(
                                  s1,
                                  e1,
                                  NA_BAND_FILL,
                                  `na-a-${dayKey}`,
                                  "NA"
                                );
                                const s2 = Date.UTC(
                                  y,
                                  m,
                                  day + 1,
                                  0,
                                  0,
                                  0
                                );
                                const e2 = Date.UTC(
                                  y,
                                  m,
                                  day + 1,
                                  NA_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s2,
                                  e2,
                                  NA_BAND_FILL,
                                  `na-b-${dayKey}`,
                                  "NA"
                                );
                              }
                            }
                            return out;
                          })()}

                      {/* Area series for each game */}
{chartGames.map((g, idx) => {
  const id = String(g.metadata.app_id);
  const color = seriesColor(idx);
  if (!renderData.some(row => row[id] != null)) return null;

  // Normalize highlightedSeries into an array
  const selectedIds = Array.isArray(highlightedSeries)
    ? highlightedSeries
    : highlightedSeries
    ? [String(highlightedSeries)]
    : [];

  const isDimmed =
    selectedIds.length > 0 && !selectedIds.includes(id);

  return (
    <Area
      key={id}
      type="monotone"
      dataKey={id}
      stroke={color}
      fill={color}
      fillOpacity={isDimmed ? 0.08 : 0.25}
      strokeWidth={isDimmed ? 1.4 : 2}
      strokeOpacity={isDimmed ? 0.4 : 1}
      dot={false}
      name={g.metadata.name}
      {...seriesIntro(id)}
      onClick={() => {
  setHighlightedSeries((prev) => {
    const prevArr = Array.isArray(prev)
      ? prev
      : prev
      ? [String(prev)]
      : [];

    const already = prevArr.includes(id);
    const multi = multiKeyDown; // 👈 comes from the global key tracker

    if (multi) {
      // Ctrl/Cmd + click: toggle this series in the highlighted set
      if (already) {
        const next = prevArr.filter((x) => x !== id);
        return next.length ? next : null;
      }
      return [...prevArr, id];
    } else {
      // Normal click: single-select, or clear if it was the only one
      if (already && prevArr.length === 1) {
        return null;
      }
      return [id];
    }
  });
}}
      style={{ cursor: "pointer" }}
    />
  );
})}

                      </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

                {/* Window slider */}
                {mergedData.length > 1 && (
                  <div className="mg-steam-window-slider" style={{ margin: "12px 40px 18px 98px", padding: "8px 0", flex: "0 0 auto" }}>
                    <style>{`
                      .mg-steam-range {
                        -webkit-appearance: none;
                        appearance: none;
                      }
                      .mg-steam-range::-webkit-slider-runnable-track {
                        height: 18px;
                        background: transparent;
                        border: none;
                      }
                      .mg-steam-range::-moz-range-track {
                        height: 18px;
                        background: transparent;
                        border: none;
                      }
                      .mg-steam-range::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        appearance: none;
                        width: 28px;
                        height: 14px;
                        border-radius: 7px;
                        border: 1px solid rgba(63,169,245,0.95);
                        background: #3fa9f5;
                        cursor: grab;
                        box-shadow: 0 0 10px rgba(63,169,245,0.35);
                        pointer-events: auto;
                        margin-top: 2px;
                      }
                      .mg-steam-range::-moz-range-thumb {
                        width: 28px;
                        height: 14px;
                        border-radius: 7px;
                        border: 1px solid rgba(63,169,245,0.95);
                        background: #3fa9f5;
                        cursor: grab;
                        box-shadow: 0 0 10px rgba(63,169,245,0.35);
                      }
                    `}</style>
                    <div
                      style={{
                        position: "relative",
                        height: 6,
                        background: "#1a1a1a",
                        borderRadius: 4,
                      }}
                    >
                      <div
                        className="mg-steam-range"
                        style={{
                          position: "absolute",
                          left: `${
                            (100 * winStartIdx) /
                            (mergedData.length - 1)
                          }%`,
                          width: `${
                            (100 * (winEndIdx - winStartIdx)) /
                            Math.max(1, mergedData.length - 1)
                          }%`,
                          top: 0,
                          bottom: 0,
                          background: "#3fa9f5",
                          opacity: 0.35,
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <div style={{ position: "relative", height: 18, marginTop: -12 }}>
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, mergedData.length - 1)}
                        value={winStartIdx}
                        onChange={(e) =>
                          setWinStartIdx(
                            Math.min(
                              parseInt(e.target.value, 10),
                              winEndIdx - 1
                            )
                          )
                        }
                        className="mg-steam-range"
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          width: "100%",
                          height: 18,
                          WebkitAppearance: "none",
                          background: "transparent",
                          margin: 0,
                          pointerEvents: "none",
                        }}
                      />
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, mergedData.length - 1)}
                        value={winEndIdx}
                        onChange={(e) =>
                          setWinEndIdx(
                            Math.max(
                              parseInt(e.target.value, 10),
                              winStartIdx + 1
                            )
                          )
                        }
                        className="mg-steam-range"
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          width: "100%",
                          height: 18,
                          WebkitAppearance: "none",
                          background: "transparent",
                          margin: 0,
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  </div>
                )}
                <SteamChartLegend games={chartPeakGames} chartGames={chartGames} highlighted={highlightedSeries}
                  colorForIndex={seriesColor} onRemove={handleRemoveFromChart}
                  onToggle={(id, multi) => setHighlightedSeries(previous => {
                    const ids = Array.isArray(previous) ? previous : previous ? [String(previous)] : [];
                    if (!multi) return ids.length === 1 && ids[0] === id ? null : [id];
                    const next = ids.includes(id) ? ids.filter(value => value !== id) : [...ids, id];
                    return next.length ? next : null;
                  })} />
              </>
          </div>
          <button
            type="button"
            onPointerDown={handleSteamChartResizeStart}
            title="Drag to resize chart height"
            aria-label="Drag to resize chart height"
            style={{
              position: "absolute",
              right: 6,
              bottom: 6,
              zIndex: 6,
              width: 20,
              height: 20,
              border: "none",
              background: "transparent",
              color: "#8a96a3",
              display: "grid",
              placeItems: "center",
              cursor: "ns-resize",
              touchAction: "none",
              padding: 0,
            }}
          >
            <Grip size={14} aria-hidden="true" />
          </button>
          </div>
        )}


        {/* Content */}
        <DashboardContent label="Steam" loading={initialDashboardLoading}>
        {viewMode === "cards" && <MGSectionHeader index={chartGames.length ? "02" : "01"} accent="paper">
          Ranked games / {mode === "players" ? "Live players" : "Followers"}
        </MGSectionHeader>}
        {mode === "players" ? (
          viewMode === "market" ? (
           // ============ Market Mind ============
<div
  ref={bubbleContainerRef}
  className="mm-surface"
  style={{
    position: "absolute",
    inset: "52px 0 20px",
    zIndex: 0,
  }}
>
  <style>{`.mm-bubble-dim{opacity:.25}.mm-bubble-hit{opacity:1}`}</style>

  {/* Skeleton until full-size layout is ready (mirrors mindSHARE) */}
{showMarketSkeleton ? (
  <DashboardLoader label="Steam Market Mind" />
) : marketLayoutError ? (
  <div role="alert" style={{ padding: 20, color: "#ff9b9b" }}>
    Market Mind could not load: {marketLayoutError}{" "}
    <button type="button" onClick={() => setMarketLayoutRetry(value => value + 1)}>Retry Market Mind</button>
  </div>
) : (
  <>
          <svg
            ref={bubbleSvgRef}
            width={dimensions.width}
            height={dimensions.height}
            style={{ display: "block" }}
          >
            <g id="steam-mm-zoom" transform={zoomT.toString()}>
              <SteamMarketMarks nodes={bubbleNodes} squareMode={marketShape === "squares"}
                zoom={zoomT} selected={marketSelected}
                onHover={setBubbleHovered} onSelect={setMarketSelected} width={dimensions.width} />
            </g>
          </svg>

          {(marketSelected || bubbleHovered) &&
            (() => {
              const bubbleHovered = activeMarketNode;
              const screenX =
                zoomT?.applyX?.(bubbleHovered.x) ?? bubbleHovered.x;
              const screenY =
                zoomT?.applyY?.(bubbleHovered.y) ?? bubbleHovered.y;

              const pos = computePopoverPos({
                x: screenX,
                y: screenY,
                r: bubbleHovered.r || 40,
                containerWidth: dimensions.width,
                containerHeight: dimensions.height,
              });

              return (
                <div
                  style={{
                    position: "absolute",
                    left: pos.left,
                    top: pos.top,
                    background: "#14191b",
                    color: "#fff",
                    padding: 0,
                    borderRadius: 12,
                    boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
                    zIndex: 1000,
                    width: 320,
                    pointerEvents: marketSelected ? "auto" : "none",
                    border: "1px solid #333",
                    overflow: "hidden",
                  }}
                  className="steam-market-detail"
                >
              {marketSelected && <button type="button" aria-label="Close game details"
                onClick={() => setMarketSelected(null)}
                style={{ position: "absolute", right: 8, top: 8, zIndex: 2, color: "#fff", background: "#000", border: "1px solid #444", cursor: "pointer" }}>Close</button>}
              {bubbleHovered.data.box_art ? (
                <img
                  src={bubbleHovered.data.box_art}
                  alt={bubbleHovered.data.name}
                  style={{ width: "100%", display: "block" }}
                />
              ) : null}
              <div style={{ height: 8, background: "#14191b" }} />
              {(() => {
                const embed = getVideoEmbed(bubbleHovered.data.video);
                if (!embed) return null;
                return (
                  <SteamVideoPreview
                    tags={bubbleHovered.data.tags}
                    style={{ width: "100%", background: "#000" }}
                  >
                      {embed.type === "file" ? (
                        <video
                          src={embed.src}
                          autoPlay
                          muted
                          loop
                          playsInline
                          style={{ width: "100%", height: "auto", display: "block" }}
                        />
                      ) : (
                        <iframe
                          src={embed.src}
                          title={`${bubbleHovered.data.name} trailer`}
                          frameBorder="0"
                          allow="autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                          style={{ width: "100%", aspectRatio: "16/9", display: "block" }}
                        />
                      )}
                  </SteamVideoPreview>
                );
              })()}
              <div
                style={{
                  padding: 12,
                  borderTop: "1px solid #333",
                  background: "#14191b",
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
                  {bubbleHovered.data.name}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
                  {bubbleHovered.data.ccu.toLocaleString()} CCU
                </div>
                {bubbleHovered.data.short_description && (
                  <div style={{ fontSize: 13, color: "#ddd", marginBottom: 8 }}>
                    {bubbleHovered.data.short_description}
                  </div>
                )}
                {(bubbleHovered.data.release_date || formatPrice(bubbleHovered.data)) && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 6 }}>
    {bubbleHovered.data.release_date && (
      <div style={{ marginBottom: formatPrice(bubbleHovered.data) ? 6 : 0 }}>
        <strong>Release:</strong>{" "}
        {new Date(bubbleHovered.data.release_date).toLocaleDateString()}
      </div>
    )}
    {formatPrice(bubbleHovered.data) && (
      <div>
        <strong>Price:</strong> {formatPrice(bubbleHovered.data)}
      </div>
    )}
  </div>
)}

{bubbleHovered.data.publishers && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 4 }}>
    <strong>Publisher:</strong> {bubbleHovered.data.publishers}
  </div>
)}
{bubbleHovered.data.developers && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 8 }}>
    <strong>Developer:</strong> {bubbleHovered.data.developers}
  </div>
)}

                {bubbleHovered.data.tags?.length ? (
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    {bubbleHovered.data.tags.join(", ")}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })()}
    </>
  )}
</div>

          ) : viewMode === "effervescent" ? (
            // ============ Effervescent ============
            <div
              ref={effContainerRef}
              style={{
                position: "relative",
                height: effHeight,
                overflow: "hidden",
              }}
            >
              <EffervescentBubbles
                key={effKey}
                games={visiblePlayers}
                onHoverChange={setEffHover}
              />

              {/* Smart-positioned hover popout */}
              {effHover &&
                effHover.x != null &&
                effHover.y != null &&
                (() => {
                  const rect = effContainerRef.current?.getBoundingClientRect();
                  const containerWidth =
                    rect?.width ||
                    document.querySelector("[data-eff-dim]")?.clientWidth ||
                    1600;
                  const containerHeight = rect?.height || effHeight;
                  const pos = computePopoverPos({
                    x: effHover.x,
                    y: effHover.y,
                    r: effHover.r || 40,
                    containerWidth,
                    containerHeight,
                  });
                  return (
                    <div
                      style={{
                        position: "absolute",
                        left: pos.left,
                        top: pos.top,
                        background: "#222",
                        color: "#fff",
                        padding: 0,
                        borderRadius: 12,
                        boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
                        zIndex: 1000,
                        width: 320,
                        pointerEvents: "none",
                        border: "1px solid #333",
                        overflow: "hidden",
                      }}
                    >
                      {effHover.data.box_art ? (
                        <img
                          src={effHover.data.box_art}
                          alt={effHover.data.name}
                          style={{ width: "100%", display: "block" }}
                        />
                      ) : null}
                      <div style={{ height: 8, background: "#222" }} />
                      {(() => {
                        const embed = getVideoEmbed(effHover.data.video);
                        if (!embed) return null;
                        return (
                          <SteamVideoPreview
                            tags={effHover.data.tags}
                            style={{ width: "100%", background: "#000" }}
                          >
                              {embed.type === "file" ? (
                                <video
                                  src={embed.src}
                                  autoPlay
                                  muted
                                  loop
                                  playsInline
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                  }}
                                />
                              ) : (
                                <iframe
                                  src={embed.src}
                                  title={`${effHover.data.name} trailer`}
                                  frameBorder="0"
                                  allow="autoplay; encrypted-media; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    width: "100%",
                                    aspectRatio: "16/9",
                                    display: "block",
                                  }}
                                />
                              )}
                          </SteamVideoPreview>
                        );
                      })()}
                      <div
                        style={{
                          padding: 12,
                          borderTop: "1px solid #333",
                          background: "#222",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 16,
                            marginBottom: 6,
                          }}
                        >
                          {effHover.data.name}
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 800,
                            marginBottom: 8,
                          }}
                        >
                          {(effHover.data.ccu || 0).toLocaleString()} CCU
                        </div>
                        {effHover.data.short_description && (
                          <div
                            style={{
                              fontSize: 13,
                              color: "#ddd",
                              marginBottom: 8,
                            }}
                          >
                            {effHover.data.short_description}
                          </div>
                        )}
                      {(effHover.data.release_date || formatPrice(effHover.data)) && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 6 }}>
    {effHover.data.release_date && (
      <div style={{ marginBottom: formatPrice(effHover.data) ? 6 : 0 }}>
        <strong>Release:</strong>{" "}
        {new Date(effHover.data.release_date).toLocaleDateString()}
      </div>
    )}
    {formatPrice(effHover.data) && (
      <div>
        <strong>Price:</strong> {formatPrice(effHover.data)}
      </div>
    )}
  </div>
)}


                        {effHover.data.publishers && (
                          <div
                            style={{
                              fontSize: 13,
                              color: "#ccc",
                              marginBottom: 4,
                            }}
                          >
                            <strong>Publisher:</strong>{" "}
                            {effHover.data.publishers}
                          </div>
                        )}
                        {effHover.data.developers && (
                          <div
                            style={{
                              fontSize: 13,
                              color: "#ccc",
                              marginBottom: 8,
                            }}
                          >
                            <strong>Developer:</strong>{" "}
                            {effHover.data.developers}
                          </div>
                        )}
                        {effHover.data.tags?.length ? (
                          <div style={{ fontSize: 11, color: "#aaa" }}>
                            {effHover.data.tags.join(", ")}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              {/* invisible size anchor */}
              <div
                data-eff-dim
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
                   ) : (
            // ============ Players Cards ============
            <Fragment key="players-cards">
              <div
                ref={isPublicPreview ? previewWrapRef : undefined}
                className={isPublicPreview ? "mg-mindshare-preview-wrap" : undefined}
                style={isPublicPreview && previewMetrics?.minHeight ? { minHeight: previewMetrics.minHeight } : undefined}
              >
              <div
                data-steam-grid={isPublicPreview || undefined}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "12px",
                  justifyContent: "center",
                  overflow: "visible",
                }}
              >
                {(isPublicPreview ? visiblePlayers.slice(0, previewMetrics?.totalCount ?? 70) : visiblePlayers).map((g, i) => {
                  const share =
                    totalCcu > 0 ? ((g.ccu / totalCcu) * 100).toFixed(2) : "0.00";
                  const hovered = hoveredCards[g.app_id] || false;
                  const isRecent = releasedWithin7Days(g.release_date);
                  const isInChart = chartGames.some(
                    (cg) => String(cg.metadata.app_id) === String(g.app_id)
                  );
                  // parsed metadata for clickable filters (kept for future use)
                  const publisherNames = splitPublishersSafe(g.publishers || "");
                  const developerNames = splitDevelopersSafe(g.developers || "");
                  const tagList = Array.isArray(g.tags) ? g.tags : [];

                  const previewGhost = Boolean(isPublicPreview && previewMetrics && i >= previewMetrics.visibleCount);
                  return (
                    <div
                      key={g.app_id}
                      data-steam-card={isPublicPreview || undefined}
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "360px",
                        margin: "0 auto",
                        ...(previewGhost ? { pointerEvents: "none" } : null),
                      }}
                      onMouseLeave={() => {
                        // leaving both the card and its popout → clear hover
                        setHoveredCards({});
                      }}
                    >
                      {/* Gold glow */}
                      <GoldGlow active={isRecent} subtle={isReleasedThisWeek} />

                      {/* Card wrapper */}
                      <div
                        ref={(el) => (cardRefs.current[g.app_id] = el)}
                        style={{
                          borderRadius: "12px",
                          overflow: "visible",
                          background: "transparent",
                          width: "100%",
                          position: "relative",
                          cursor: "default",
                          zIndex: hovered ? 1000 : 0,
                        }}
                        onMouseEnter={() => {
                          setHoveredCards({ [g.app_id]: true });
                          const card = cardRefs.current[g.app_id];
                          if (card) {
                            card.style.setProperty("--sx", "50%");
                            card.style.setProperty("--sy", "50%");
                          }
                        }}
                        onMouseLeave={() => {
                          handleMouseLeave(g.app_id);
                          setTimeout(() => {
                            setHoveredCards((prev) => {
                              if (prev[g.app_id]) return prev;
                              return { ...prev, [g.app_id]: false };
                            });
                          }, 80);
                        }}
                      >
                      <div
  className="mg-game-art-card steam-art-card"
  style={{
    borderRadius: "2px",
    overflow: "hidden",
    background: "#000",
    position: "relative",
    zIndex: 1,
  }}
>

                          {/* Base gradient skin */}
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: "14px",
                              background:
                                "linear-gradient(135deg, rgba(0,153,255,0.18), rgba(0,153,255,0.02))",
                              boxShadow:
                                "0 18px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(63,169,245,0.12)",
                              filter: "saturate(110%)",
                              pointerEvents: "none",
                              zIndex: 0,
                            }}
                          />

                          {/* Subtle inner gleam when recent */}
                          {isRecent && (
                            <div
                              aria-hidden
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "14px",
                                pointerEvents: "none",
                                zIndex: 4,
                                boxShadow:
                                  "inset 0 0 0 1px rgba(255,215,0,0.15)",
                                background:
                                  "radial-gradient(600px 340px at var(--sx,50%) var(--sy,50%), rgba(255,215,0,0.08), transparent 45%)",
                                mixBlendMode: "screen",
                              }}
                            />
                          )}

                          {/* Shine overlay */}
                          <div
                            style={getShineStyle(
                              hovered,
                              cardRefs.current[g.app_id]
                            )}
                          />

                          {/* Box art */}
                          <img
                            src={g.box_art}
                            alt={g.name}
                            loading={i < 10 ? "eager" : "lazy"}
                            decoding="async"
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "230/107",
                              objectFit: "cover",
                              position: "relative",
                              zIndex: 2,
                            }}
                          />

                          {/* Rank badge */}
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              background: "rgba(0,0,0,0.55)",
                              color: "#fff",
                              borderRadius: 0,
                              padding: "0 6px",
                              width: "auto",
                              minWidth: 28,
                              boxSizing: "border-box",
                              whiteSpace: "nowrap",
                              height: "28px",
                              lineHeight: 1,
                              fontSize: "12px",
                              fontWeight: 700,
                              zIndex: 3,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            #{i + 1}
                          </div>

                          {/* Close button */}
                          {!isPublicPreview && <button
                            style={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              width: "28px",
                              height: "28px",
                              background: "rgba(0,0,0,0.55)",
                              border: "none",
                              fontSize: "12px",
                              color: "#fff",
                              cursor: "pointer",
                              borderRadius: 0,
                              padding: 0,
                              lineHeight: 1,
                              fontWeight: 700,
                              zIndex: 3,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            type="button"
                            aria-label="Hide card"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemove(g.app_id);
                            }}
                          >
                            ×
                          </button>}

                          {/* Footer – entire bar adds to chart */}
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: 0,
                              padding: "0 0 0 10px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-end",
                              background:
                                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)",
                              zIndex: 2,
                              cursor: isPublicPreview ? "default" : "pointer",
                            }}
                            onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPublicPreview) return;
    if (isInChart) {
      handleRemoveFromChart(g.app_id);   // 🔹 remove if already in chart
    } else {
      handleAddToChart(g);               // 🔹 add if not in chart
    }
  }}
>
                            {/* Left: CCU + share */}
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#cfd8e3",
                                whiteSpace: "nowrap",
                                fontWeight: 800,
                                lineHeight: 1,
                                paddingBottom: 5,
                                textShadow: "0 1px 4px rgba(0,0,0,0.95)",
                              }}
                              aria-label="Current CCU and share of visible total"
                            >
                              {g.ccu?.toLocaleString()} · {share}%
                            </div>

                            {/* Right: Twitch-style compare button */}
                            {!isPublicPreview && <button
                              type="button"
                              title={
                                isInChart
                                  ? "Remove from Player History chart"
                                  : "Add to Player History chart"
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isInChart) {
                                  handleRemoveFromChart(g.app_id);
                                } else {
                                  handleAddToChart(g);
                                }
                              }}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 0,
                                border: "none",
                                background: isInChart
                                  ? "#3fa9f5"
                                  : "rgba(0,0,0,0.55)",
                                color: isInChart ? "#000" : "#cfd8e3",
                                fontSize: 14,
                                lineHeight: "15px",
                                fontWeight: 900,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "background 0.15s ease",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                                backdropFilter: "blur(4px)",
                                flexShrink: 0,
                              }}
                            >
                              {isInChart ? "✓" : "+"}
                            </button>}
                          </div>
                        </div>
                      </div>

                      {/* Hover popout with hoverable gap wrapper */}
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          width: "100%",
                          transform: "none",
                          boxSizing: "border-box",
                          paddingTop: 10, // this is the visual gap, but still hover area
                          zIndex: 1000,
                          opacity: hovered ? 1 : 0,
                          pointerEvents: hovered ? "auto" : "none",
                          transition: "opacity 160ms ease",
                        }}
                        onMouseEnter={() => {
                          setHoveredCards({ [g.app_id]: true });
                        }}
                        onMouseLeave={() => {
                          setHoveredCards((prev) => ({
                            ...prev,
                            [g.app_id]: false,
                          }));
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            background: "rgba(34,34,34,0.96)",
                            borderRadius: "12px",
                            padding: "10px 12px",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.6)",
                            fontSize: "14px",
                            lineHeight: 1.4,
                            border: "1px solid #333",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          {/* Video embed */}
                          {hovered &&
                            g.video &&
                            (() => {
                              const embed = getVideoEmbed(g.video);
                              if (!embed) return null;
                              return (
                                <SteamVideoPreview
                                  tags={g.tags}
                                  style={{
                                    width: "100%",
                                    aspectRatio: "16/9",
                                    background: "#000",
                                    borderRadius: "10px",
                                    border: "1px solid #333",
                                    overflow: "hidden",
                                    marginBottom: "10px",
                                  }}
                                >
                                    {embed.type === "file" ? (
                                      <video
                                        src={embed.src}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          display: "block",
                                          pointerEvents: "none",
                                        }}
                                      />
                                    ) : (
                                      <iframe
                                        src={embed.src}
                                        title={`${g.name} trailer`}
                                        frameBorder="0"
                                        allow="encrypted-media; picture-in-picture"
                                        allowFullScreen
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          display: "block",
                                          pointerEvents: "none",
                                        }}
                                      />
                                    )}
                                </SteamVideoPreview>
                              );
                            })()}

                          <div
                            style={{
                              position: "relative",
                              margin: "0 0 11px",
                              padding: "0 0 9px 13px",
                              color: "#fff",
                              fontSize: "15px",
                              fontWeight: 900,
                              lineHeight: 1.2,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              textShadow: "0 1px 10px rgba(63,169,245,0.22)",
                              borderBottom: "1px solid rgba(63,169,245,0.34)",
                            }}
                          >
                            <span
                              aria-hidden
                              style={{
                                position: "absolute",
                                left: 0,
                                top: 1,
                                bottom: 10,
                                width: 3,
                                borderRadius: 3,
                                background: "linear-gradient(180deg, #75d5ff, #3fa9f5 55%, #2672d8)",
                                boxShadow: "0 0 12px rgba(63,169,245,0.65)",
                              }}
                            />
                            {g.name}
                          </div>

                          {/* Game info */}
                          {g.short_description && (
                            <div style={{ marginBottom: 10, color: "#fff" }}>
                              {g.short_description}
                            </div>
                          )}

                          {(g.release_date || formatPrice(g)) && (
                            <div style={{ marginBottom: 6, color: "#cfd8e3" }}>
                              {g.release_date && (
                                <div
                                  style={{
                                    marginBottom: formatPrice(g) ? 6 : 0,
                                  }}
                                >
                                  <strong style={{ color: "#fff" }}>
                                    Release:
                                  </strong>{" "}
                                  {new Date(
                                    g.release_date
                                  ).toLocaleDateString()}
                                </div>
                              )}
                              {formatPrice(g) && (
                                <div>
                                  <strong style={{ color: "#fff" }}>
                                    Price:
                                  </strong>{" "}
                                  {formatPrice(g)}
                                </div>
                              )}
                            </div>
                          )}

                          {g.publishers && (
                            <div
                              style={{ marginBottom: 6, color: "#cfd8e3" }}
                            >
                              <strong style={{ color: "#fff" }}>
                                Publisher:
                              </strong>{" "}
                              {g.publishers}
                            </div>
                          )}

                          {g.developers && (
                            <div
                              style={{ marginBottom: 10, color: "#cfd8e3" }}
                            >
                              <strong style={{ color: "#fff" }}>
                                Developer:
                              </strong>{" "}
                              {g.developers}
                            </div>
                          )}

                          {Array.isArray(g.tags) && g.tags.length > 0 && (
                            <div style={{ color: "#cfd8e3" }}>
                              <strong style={{ color: "#fff" }}>Tags:</strong>{" "}
                              {g.tags.join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasMorePlayers && !isPublicPreview && (
                <div
                  ref={sentinelRef}
                  style={{ height: "40px", background: "transparent" }}
                />
              )}
              {isPublicPreview && previewMetrics?.veilTop > 0 && (
                <>
                  <div className="mg-mindshare-preview-veil" style={{ top: previewMetrics.veilTop }} aria-hidden="true" />
                  <div
                    className="mg-mindshare-preview-cta"
                    style={{ top: previewMetrics.ctaTop }}
                    role="note"
                    aria-label="Steam full access"
                  >
                    <Lock size={22} aria-hidden="true" />
                    <h3>Full Steam access for paying members</h3>
                    <p>
                      Full rankings, filters, and chart tools are available to members.
                      Contact <a href="mailto:info@mindgamedata.com">info@mindgamedata.com</a> for details.
                    </p>
                  </div>
                </>
              )}
              </div>
            </Fragment>
          )

        ) : (
          // ============ Followers Cards ============
          <Fragment key="followers-cards">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "12px",
                justifyContent: "center",
                overflow: "visible",
              }}
            >
              {visibleFollowers.map((g, i) => {
                const growth7 = g.growth_7d ?? 0;
                const share = `${g.followers?.toLocaleString()} · ${
                  growth7 >= 0 ? "+" : ""
                }${Math.abs(growth7).toLocaleString()} (7D)`;
                const hovered = hoveredCards[g.app_id] || false;
                const isRecent = releasedWithin7Days(g.release_date);
// 🔹 ADD THIS:
  const isInChart = chartGames.some(
    (cg) => String(cg.metadata.app_id) === String(g.app_id)
  );

                return (
                  <div
                    key={g.app_id}
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "360px",
                      margin: "0 auto",
                    }}
                  >
                    {/* NEW: gold outer glow */}
                    <GoldGlow active={isRecent} subtle={isReleasedThisWeek} />

                    {/* Card wrapper */}
                    <div
  ref={(el) => (cardRefs.current[g.app_id] = el)}
  style={{
    borderRadius: "12px",
    overflow: "visible",
    background: "transparent",
    width: "100%",
    position: "relative",
    cursor: "default",   // match Players cards
    zIndex: hovered ? 1000 : 0,
  }}
  onMouseEnter={() => {
                        setHoveredCards((prev) => ({
                          ...prev,
                          [g.app_id]: true,
                        }));
                        const card = cardRefs.current[g.app_id];
                        if (card) {
                          card.style.setProperty("--sx", "50%");
                          card.style.setProperty("--sy", "50%");
                        }
                      }}
                      onMouseLeave={() => {
                        setHoveredCards((prev) => ({
                          ...prev,
                          [g.app_id]: false,
                        }));
                        handleMouseLeave(g.app_id);
                      }}
                    >
                      <div
                        className="mg-game-art-card steam-art-card"
                        style={{
                          borderRadius: "2px",
                          overflow: "hidden",
                          background: "#000",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {/* Base layer */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "14px",
                            background:
                              "linear-gradient(135deg, rgba(0,153,255,0.18), rgba(0,153,255,0.02))",
                            boxShadow:
                              "0 18px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(63,169,245,0.12)",
                            filter: "saturate(110%)",
                            pointerEvents: "none",
                            zIndex: 0,
                          }}
                        />

                        {/* NEW: very subtle inner gleam when recent */}
                        {isRecent && (
                          <div
                            aria-hidden
                            style={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: "14px",
                              pointerEvents: "none",
                              zIndex: 4,
                              boxShadow: "inset 0 0 0 1px rgba(255,215,0,0.15)",
                              background:
                                "radial-gradient(600px 340px at var(--sx,50%) var(--sy,50%), rgba(255,215,0,0.08), transparent 45%)",
                              mixBlendMode: "screen",
                            }}
                          />
                        )}

                        {/* Shine */}
                        <div
                          style={getShineStyle(
                            hovered,
                            cardRefs.current[g.app_id]
                          )}
                        />

                        {/* Image */}
                        <img
                          src={g.box_art}
                          alt={g.name}
                          style={{
                            display: "block",
                            width: "100%",
                            aspectRatio: "230/107",
                            objectFit: "cover",
                            position: "relative",
                            zIndex: 2,
                          }}
                        />

                        {/* Rank */}
                        <div
                          style={{
                            position: "absolute",
                              top: 0,
                              left: 0,
                              background: "rgba(0,0,0,0.55)",
                              color: "#fff",
                              borderRadius: 0,
                              padding: "0 6px",
                              width: "auto",
                              minWidth: 28,
                              boxSizing: "border-box",
                              whiteSpace: "nowrap",
                              height: 28,
                              lineHeight: 1,
                              fontSize: "12px",
                              fontWeight: 700,
                              zIndex: 3,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                          }}
                        >
                          #{i + 1}
                        </div>

                        {/* Close button */}
                        <button
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 28,
                            height: 28,
                            background: "rgba(0,0,0,0.55)",
                            border: "none",
                            fontSize: "12px",
                            color: "#fff",
                            cursor: "pointer",
                            borderRadius: 0,
                            padding: 0,
                            lineHeight: 1,
                            fontWeight: 700,
                            zIndex: 3,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveFollower(g.app_id);
                          }}
                        >
                          ×
                        </button>
                       {/* Footer */}
<div
  style={{
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: "0 0 0 10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    background:
      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)",
    zIndex: 2,
    cursor: "pointer",
  }}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation(); // don't bubble to the card
    if (isInChart) {
      handleRemoveFromChart(g.app_id);
    } else {
      handleAddToChart(g); // Followers mode: loads followers series
    }
  }}
>

  {/* Left: Followers + 7D growth line */}
  <div
    style={{
      fontSize: "13px",
      color: "#cfd8e3",
      whiteSpace: "nowrap",
      fontWeight: 800,
      lineHeight: 1,
      paddingBottom: 5,
      textShadow: "0 1px 4px rgba(0,0,0,0.95)",
    }}
  >
    {share}
  </div>

  {/* Right: Twitch-style compare button */}
  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
    }}
  >
    <button
      type="button"
      title={
        isInChart
          ? "Remove from Followers chart"
          : "Add to Followers chart"
      }
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isInChart) {
          handleRemoveFromChart(g.app_id);
        } else {
          handleAddToChart(g);
        }
      }}
      style={{
        width: 28,
        height: 28,
        borderRadius: 0,
        border: "none",
        background: isInChart ? "#3fa9f5" : "rgba(0,0,0,0.55)",
        color: isInChart ? "#000" : "#cfd8e3",
        fontSize: 14,
        lineHeight: "15px",
        fontWeight: 900,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.15s ease",
        boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)",
      }}
    >
      {isInChart ? "✓" : "+"}
    </button>
  </div>
</div>

                      </div>
                    </div>

                  {/* Info box */}
<div
  style={{
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    width: "100%",
    transform: "none",
    boxSizing: "border-box",
    // hoverable zone including the visual gap
    paddingTop: 12,
    zIndex: 2000,
    opacity: hovered ? 1 : 0,
    pointerEvents: hovered ? "auto" : "none",
    transition: "opacity 160ms ease",
  }}
  onMouseEnter={() => {
    setHoveredCards((prev) => ({ ...prev, [g.app_id]: true }));
  }}
  onMouseLeave={() => {
    setHoveredCards((prev) => ({ ...prev, [g.app_id]: false }));
  }}
>
  <div
    style={{
      width: "100%",
      boxSizing: "border-box",
      background: "rgba(34,34,34,0.96)",
      borderRadius: "12px",
      padding: "10px 12px",
      boxShadow: "0 12px 28px rgba(0,0,0,0.6)",
      fontSize: "14px",
      lineHeight: 1.4,
      border: "1px solid #333",
      backdropFilter: "blur(10px)",
      pointerEvents: "none", // keep the panel itself non-interactive
    }}
  >
    <div
      style={{
        position: "relative",
        margin: "0 0 11px",
        padding: "0 0 9px 13px",
        color: "#fff",
        fontSize: "15px",
        fontWeight: 900,
        lineHeight: 1.2,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        textShadow: "0 1px 10px rgba(63,169,245,0.22)",
        borderBottom: "1px solid rgba(63,169,245,0.34)",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: 1,
          bottom: 10,
          width: 3,
          borderRadius: 3,
          background: "linear-gradient(180deg, #75d5ff, #3fa9f5 55%, #2672d8)",
          boxShadow: "0 0 12px rgba(63,169,245,0.65)",
        }}
      />
      {g.name}
    </div>

    {/* Video embed (Followers: prefer microtrailer, then fallback to video) */}
    {hovered &&
      (g.microtrailer ||
        g.micro_trailer ||
        g.micro_trailer_url ||
        g.video) &&
      (() => {
        const rawUrl =
          g.microtrailer ||
          g.micro_trailer ||
          g.micro_trailer_url ||
          g.video;

        const embed = getVideoEmbed(rawUrl);
        if (!embed) return null;

        return (
          <SteamVideoPreview
            tags={g.tags}
            style={{
              width: "100%",
              aspectRatio: "16/9",
              background: "#000",
              borderRadius: "10px",
              border: "1px solid #333",
              overflow: "hidden",
              marginBottom: "10px",
            }}
          >
              {embed.type === "file" ? (
                <video
                  src={embed.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              ) : (
                <iframe
                  src={embed.src}
                  title={`${g.name} trailer`}
                  frameBorder="0"
                  allow="encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              )}
          </SteamVideoPreview>
        );
      })()}

    {/* Game info */}
    {g.short_description && (
      <div style={{ marginBottom: 10, color: "#fff" }}>
        {g.short_description}
      </div>
    )}

    {(g.release_date || formatPrice(g)) && (
      <div style={{ marginBottom: 6, color: "#cfd8e3" }}>
        {g.release_date && (
          <div style={{ marginBottom: formatPrice(g) ? 6 : 0 }}>
            <strong style={{ color: "#fff" }}>Release:</strong>{" "}
            {new Date(g.release_date).toLocaleDateString()}
          </div>
        )}
        {formatPrice(g) && (
          <div>
            <strong style={{ color: "#fff" }}>Price:</strong>{" "}
            {formatPrice(g)}
          </div>
        )}
      </div>
    )}

    {g.publishers && (
      <div style={{ marginBottom: 6, color: "#cfd8e3" }}>
        <strong style={{ color: "#fff" }}>Publisher:</strong>{" "}
        {g.publishers}
      </div>
    )}

    {g.developers && (
      <div style={{ marginBottom: 10, color: "#cfd8e3" }}>
        <strong style={{ color: "#fff" }}>Developer:</strong>{" "}
        {g.developers}
      </div>
    )}

    {Array.isArray(g.tags) && g.tags.length > 0 && (
      <div style={{ color: "#cfd8e3" }}>
        <strong style={{ color: "#fff" }}>Tags:</strong>{" "}
        {g.tags.join(", ")}
      </div>
    )}
  </div>
</div>


                  </div>
                );
              })}
            </div>

            {hasMoreFollowers && (
              <div
                ref={followersSentinelRef}
                style={{ height: "40px", background: "transparent" }}
              />
            )}
          </Fragment>
        )}
        </DashboardContent>
      </div>
    </div>
  );
}

export default Steam;
